import { Button } from "../../Components/UI/button";
import { Dialog, DialogContent } from "../../Components/UI/dialog";
import { Input } from "../../Components/UI/input";
import { Label } from "../../Components/UI/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../Components/UI/select";
import { Textarea } from "../../Components/UI/textarea";
import { TimeInputWithDialog } from "../../Components/Common/TimePickerWatch";
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import { DatePicker } from "../../Components/Common/DatePicker";
import MobileInput from '../../Components/Common/MobileInput';
import BusinessInput from '../../Components/Common/BusinessInput';
import TagsInput from '../../Components/Common/TagsInput';
import NameInput from '../../Components/Common/NameInput';
import { countries, states, defaultIndianCities } from "../../Components/Enums/DataEnums";
import { currentUserHasPermission, formatLabel, getConnectionByID, isMobileDevice, getUploadUrl } from "../../Utils/Utilities";
import { Plus, Settings, Trash2, X } from "lucide-react";
import { MultiSelect } from "../../Components/UI/multi-select";
import useIsMobile from "../../Hooks/useIsMobile";
import Policies from "../Settings/Policies/Policies";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { checkIfInqOnlyMode, userIsAdmin, selectConnFieldPref, isSmartSellerMode, selectThreadFieldPref } from "../../Store/Reducers";
import MobileFullScreenDropdown from "../../Components/Common/MobileDropdown";
import { format } from "date-fns";
import ExecutiveTagsInput from "../../Components/Common/ExecutiveTagsInput";
import { SHOW_CONTACT_CARD } from "../../Utils/FeatureFlags";
import ImageCropperDialog from "../../Components/Common/ImageCropper";
import { SpeechControls } from "../../Components/Common/SpeechControls";

// Reusable Tailwind classes
const conFormClasses = {
    spaceY: "space-y-1.5",
    gridBasic: "grid grid-cols-4 gap-4",
    gridResponsive: "grid grid-cols-1 sm:grid-cols-4 gap-4",
    gridFullWidth: "grid grid-cols-1 sm:grid-cols-12 gap-4",
    colSpan2: "col-span-2",
    nonSmcolSpan2: "col-span-3",
    colSpan3: "sm:col-span-3",
    colSpan4: "sm:col-span-4",
    colSpan6: "sm:col-span-6",
    colSpan1: "sm:col-span-1",
    buttonSecondary: "sahi-secondary-button",
    flexGap: "flex gap-3 pt-2 flex-wrap",
    checkbox: "h-4 w-4 rounded border-gray-300 text-[var(--sahi-primary-color)] focus:ring-[var(--sahi-primary-color)] accent-[var(--sahi-primary-color)]",
    label: "ml-1 text-sm font-medium text-gray-700 cursor-pointer"
};
const getCombinedCities = (customCities = []) => {
    const seen = new Set();
    const merged = [];
    customCities.forEach(c => {
        const lower = c.toLowerCase().trim();
        if (lower && !seen.has(lower)) {
            seen.add(lower);
            merged.push(c.trim());
        }
    });
    defaultIndianCities.forEach(c => {
        const lower = c.toLowerCase().trim();
        if (lower && !seen.has(lower)) {
            seen.add(lower);
            merged.push(c.trim());
        }
    });
    return merged.sort((a, b) => a.localeCompare(b));
};

const ConnectionFormFields = ({
    formData,
    masterData,
    uiStates,
    sectionStates,
    contactsData,
    connTypes,
    connID,
    currencySymbol,
    taxIDLabel,
    onInputChange,
    onSelectChange,
    onDateChange,
    onTimeChange,
    onConnectionTypeChange,
    onContactChange,
    onDeleteContact,
    contactToDelete,
    setContactToDelete,
    setDialogStates,
    onContactPickerClick,
    onMoreDetailsToggle,
    onSectionToggle,
    isFieldRequired,
    isFieldDisabled,
    isConnectionTypeSelected,
    setUIStates,
    onOpenMasterDialog,
    onEmailShared,
    calledFrom = '',
    // New Props for Inquiry
    isInquiryChecked = false,
    onToggleInquiry,
    isMoreDetailsChecked = false,
    onToggleMoreDetails,
    inquiryData,
    onInquiryChange,
    isStepTwo = false,
    forceShowEmail = false,
    savedThreadID = null,
    threadTypeID = 1,
    isBasic = false,
    editMode = 'ALL', // 'ALL', 'CONNECTION_ONLY', 'CONTACTS_ONLY'
    selectedContactID = null, // For filtering contacts when editing a specific contact only
	isFirstConnection = false,
    showEnterInquiry = true
}) => {
    const isMobile = useIsMobile();
    const isAdmin = useSelector(userIsAdmin);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(true);
    const [selectedFileForCrop, setSelectedFileForCrop] = useState(null);

    // Get individual field preferences from Redux
    const prefsEnabled = {
        city: useSelector((state) => selectConnFieldPref(state, "City")),
        state: useSelector((state) => selectConnFieldPref(state, "State")),
        country: useSelector((state) => selectConnFieldPref(state, "Country")),
        gstin: useSelector((state) => selectConnFieldPref(state, "TaxID")),
        potential: useSelector((state) => selectConnFieldPref(state, "Potential")),
        source: useSelector((state) => selectConnFieldPref(state, "Source")),
        since: useSelector((state) => selectConnFieldPref(state, "Since")),
        segment: useSelector((state) => selectConnFieldPref(state, "Segement")),
        poc: useSelector((state) => selectConnFieldPref(state, "POC")),
        nextTask: useSelector((state) => selectConnFieldPref(state, "NextTask")),
        notes: useSelector((state) => selectConnFieldPref(state, "Notes")),
        tags: useSelector((state) => selectConnFieldPref(state, "Tags")),
        email: useSelector((state) => selectConnFieldPref(state, "Email")),
        region: useSelector((state) => selectConnFieldPref(state, "Region"))
    };
    const emailPref = useSelector(state => selectConnFieldPref(state, "Email"));
    // Check Thread preferences for Inquiry (Type 1)
    const refPref = useSelector(state => selectThreadFieldPref(state, 'Ref', 1));
    const showThrItem = useSelector(state => selectThreadFieldPref(state, 'Item', 1));
    const showThrQty = useSelector(state => selectThreadFieldPref(state, 'Qty', 1));

    const threadStages = useSelector(state => state.masterData.threadStages);
    const inquiryStages = threadStages?.find(t => t.TID === 1)?.Stages || [];
    const isDesktopInquiry = !isMobile && !connID && (calledFrom === 'FORCE_INQUIRY' || calledFrom === 'THREAD_ENTRY') && isInquiryChecked;

    useEffect(() => {
        if (inquiryStages && inquiryStages.length > 0 && (!inquiryData?.stageID || inquiryData.stageID === null)) {
            onInquiryChange('stageID', inquiryStages[0].SID);
        }
    }, [inquiryStages, inquiryData?.stageID, onInquiryChange]);

    const { data: connectionData } = useSelector(state => state.connectionData);

    // Calculate Model Options for Step 2 usage
    const selectedProduct = masterData.products.find(p => (p.name || p.PNm) === inquiryData?.product);
    let modelOptions = [];
    if (selectedProduct) {
        try {
            const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
            if (modelsData) {
                const parsed = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                if (Array.isArray(parsed)) modelOptions = parsed.map(m => ({ value: m.mNm || m.MNm, label: m.mNm || m.MNm }));
            }
        } catch (e) { console.error(e); }
    }
    const connViewTypeID = currentUserHasPermission('ConnViewTypeID');
    const connEditPermission = currentUserHasPermission('ConnEdit');
    let dfltHideFields = {
        connType: false,
        email: false,
        moreDtls: false,
        lastInxnFields: false,
        nextTaskFields: false
    }
    const [hideFields, setHideFields] = useState(dfltHideFields);


    const calledFromArr = ['THR', 'REC', 'TSK', 'CTRC'];
    // const hideCheckboxes = calledFromArr.includes(calledFrom);
    const hideCheckboxes = true;

    useEffect(() => {
        if (calledFromArr.includes(calledFrom)) {
            setHideFields(prev => ({
                ...prev,
                moreDtls: false,
                lastInxnFields: true,
                nextTaskFields: true,
            }))
        }
        else {
            setHideFields(dfltHideFields);
        }
    }, [calledFrom]);

    // Function to check if field should be disabled based on ConnEdit permission
    const isFieldDisabledByPermission = (fieldID) => {
        // If ConnEdit is 1, all fields are enabled
        if (connEditPermission === 1) return false;

        // If ConnEdit is 2, disable fields that have prefilled data from the database
        // We need to check against the original fetched data, not the current form data
        if (connEditPermission === 2 && connID && uiStates.fetchedConnectionData) {
            const originalFieldValue = uiStates.fetchedConnectionData[fieldID];

            // Check if field had data when originally loaded from database
            if (originalFieldValue !== null && originalFieldValue !== undefined && originalFieldValue !== '') {
                // For date fields, also check if it's not an invalid date
                if (['since', 'recordDate', 'nextTaskDT'].includes(fieldID)) {
                    const dateValue = new Date(originalFieldValue);
                    return !isNaN(dateValue.getTime());
                }
                return true; // Field had data originally, so disable it
            }
        }

        return false; // Field was empty originally or ConnEdit is not 2, so enable it
    };

    const connectionFromDB = getConnectionByID(connID);
    let disableConnType = false;
    try {
        const cntThreads = connectionFromDB?.CntTypewiseThreads
            ? JSON.parse(connectionFromDB.CntTypewiseThreads)
            : [];

        const inqTypWiseObj = cntThreads.find(type => type?.TID == 2);

        if (inqTypWiseObj && Number(inqTypWiseObj.CntAll) > 1) {
            disableConnType = true;
        }
    } catch (err) {
        console.error('Error parsing CntTypewiseThreads:', err);
    }

    const searchableDropdownConfigs = {
        citySelect: {
            dataSource: getCombinedCities(masterData?.cities),
            mapper: city => ({ value: city, label: city }),
            onAddFieldName: "cities",
            searchPlaceholder: "Search City",
            addText: "Add City",
            searchEnabled: true,
            changeType: "CITY",
            searchEnabledLength: 0
        },
        sourceSelect: {
            dataSource: masterData.sources.flatMap(t =>
                t.Srcs.map(s => ({
                    value: s.Src,
                    label: s.Src,
                    TID: t.TID
                }))
            ),
            mapper: source => ({
                value: source.value,
                label: source.label,
                TID: source.TID
            }),
            onAddFieldName: "sources",
            searchPlaceholder: "Search Source",
            addText: "Add Source",
            searchEnabled: true
        },
        segmentSelect: {
            dataSource: masterData.segments,
            mapper: segment => ({
                value: segment.CNm,
                label: segment.CNm,
                color: `#${segment.Clr}`,
            }),
            onAddFieldName: "segments",
            searchPlaceholder: "Search Category",
            addText: "Add Category",
            searchEnabled: true,
            changeType: 'CAT'
        },
        recordTypeSelect: {
            dataSource: masterData.recordTypes,
            mapper: type => ({ value: type.Typ || type, label: type.Typ || type }),
            onAddFieldName: "recordTypes",
            searchPlaceholder: "Search Task Types",
            addText: "Add Type",
            searchEnabled: true
        },
        taskTypeSelect: {
            dataSource: masterData.recordTypes,
            mapper: type => ({ value: type.Typ || type, label: type.Typ || type }),
            onAddFieldName: "taskTypes",
            searchPlaceholder: "Search task types",
            addText: "Add Type",
            searchEnabled: true
        },
        executiveSelect: {
            dataSource: [...masterData.users],
            mapper: user => ({
                value: user.userID === "" ? "" : user.userID.toString(),
                label: user.name
            }),
            allowAdd: false,
            searchPlaceholder: "Search Users",
            emptyMessage: "No users found",
            sortOptions: false,
            searchEnabled: true
        },
        regionSelect: {
            dataSource: [...masterData.regions],
            mapper: region => ({ value: region.RID, label: region.Rgn }),
            allowAdd: true,
            searchPlaceholder: "Search Region",
            emptyMessage: "No regions found",
            addText: "Add new region",
            sortOptions: false,
            searchEnabled: true
        },
        stateSelect: {
            dataSource: states,
            mapper: state => ({ value: state.State, label: state.State }),
            allowAdd: false,
            searchPlaceholder: "Search State",
            emptyMessage: "No states found",
            searchEnabled: true
        },
        countrySelect: {
            dataSource: countries,
            mapper: country => ({ value: country.Country, label: country.Country }),
            allowAdd: false,
            searchPlaceholder: "Search Country",
            emptyMessage: "No countries found",
            searchEnabled: true
        }
    };

    const [emailConfigOpen, setEmailConfigOpen] = useState(false);
    const [isEmailShared, setIsEmailShared] = useState(false);
    const { ownEmail } = useSelector(state => state.masterData);

    const didMountRef = useRef(false);

    // Check if customer has email
    const hasCustomerEmail = formData?.email && formData.email.trim().length > 0;

    // Check if own email is configured
    const hasOwnEmail = ownEmail && ownEmail.length > 0 && ownEmail[0]?.length > 0;

    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

    // useEffect which sync the checkbox
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        if (!hasOwnEmail) {
            setIsEmailShared(false);
        } else if (hasCustomerEmail && hasOwnEmail) {
            setIsEmailShared(formData.isSendEmail || false);
        }

        onEmailShared(isEmailShared);
    }, [ownEmail, formData.email, formData.isSendEmail]);

    // Function which will handle checkbox of share by email
    const handleCheckboxChange = (e) => {
        if (!hasOwnEmail) {
            // No email configured → open config dialog
            setEmailConfigOpen(true);
            return;
        }

        // Toggle checkbox normally if email exists
        setIsEmailShared(e.target.checked);
        onEmailShared(e.target.checked);
    };

    // Function to handle settings icon click
    const handleSettingsClick = () => {
        setEmailConfigOpen(true);
    };

    // Render standard select field with options

    const renderSelectField = ({
        fieldID,
        label,
        placeholder,
        options,
        value,
        onChange,
        allowAdd = false,
        onAdd = null,
        getLabel = (v) => v,
        disabled = false,
    }) => {
        // const isMobile = useIsMobile(); // ✅ detect mobile once

        if (isMobile) {
            // 🔹 Mobile: use full screen dropdown
            return (
                <div key={fieldID} className={conFormClasses.spaceY}>
                    <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>
                        {label}
                    </Label>
                    <MobileFullScreenDropdown
                        options={options}
                        value={value}
                        onSelect={(val) => {
                            if (disabled) return;
                            if (val === "add" && allowAdd && onAdd) onAdd();
                            else onChange(val);
                        }}
                        placeholder={placeholder}
                        displayKey="label"
                        valueKey="value"
                        allowAdd={allowAdd}
                        addText={`Add ${label}`}
                        disabled={disabled}
                        fieldID={fieldID}
                        label={label}
                        required={isFieldRequired(fieldID)}
                    />
                </div>
            );
        }

        // 🔹 Desktop: normal Select
        return (
            <div key={fieldID} className={conFormClasses.spaceY}>
                <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>
                    {label}
                </Label>
                <Select
                    value={value}
                    onValueChange={(val) => {
                        if (disabled) return;
                        if (val === "add" && allowAdd && onAdd) onAdd();
                        else onChange(val);
                    }}
                    disabled={disabled}
                >
                    <SelectTrigger className="h-11 cursor-pointer">
                        <span className={value ? "text-foreground" : "text-gray-500"}>
                            {getLabel(value) || placeholder}
                        </span>
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px] shadow-lg">
                        {options.map((option) => (
                            <SelectItem
                                key={option.value}
                                value={option.value}
                                className="cursor-pointer"
                            >
                                {option.label}
                            </SelectItem>
                        ))}
                        {allowAdd && !disabled && (
                            <SelectItem value="add" className="cursor-pointer">
                                <div className="flex flex-row items-center space-x-1">
                                    <Plus className="h-4 w-4" />
                                    <span>{label}</span>
                                </div>
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
            </div>
        );
    };


    const renderSearchableDropdownWithDisabled = (fieldID, label, placeholder, options, config = {}) => {
        const {
            allowAdd = true,
            addText = `Add ${label}`,
            searchPlaceholder = `Search ${label.charAt(0).toUpperCase()}${label.slice(1)}`,
            emptyMessage = `No ${label.toLowerCase()} found`,
            displayKey = "label",
            valueKey = "value",
            onAddFieldName = fieldID,
            sortOptions = false,
            disabled = false,
            searchEnabled = true,
            changeType,
            searchEnabledLength = 10
        } = config;

        // Check if field should be disabled due to ConnEdit permission
        const permissionDisabled = isFieldDisabledByPermission(fieldID);
        const finalDisabled = disabled || permissionDisabled;

        let finalOptions = [...options];

        if (['pocUserID', 'interactionBy', 'nextTaskPocUserID'].includes(fieldID) && sortOptions) {
            const noneOption = finalOptions.find(opt => opt[valueKey] === "");
            const otherOptions = finalOptions.filter(opt => opt[valueKey] !== "");
            const sortedOtherOptions = otherOptions.sort((a, b) => a[displayKey].localeCompare(b[displayKey]));
            finalOptions = noneOption ? [noneOption, ...sortedOtherOptions] : sortedOtherOptions;
        } else if (sortOptions) {
            finalOptions = finalOptions.sort((a, b) => a[displayKey].localeCompare(b[displayKey]));
        }

        let currentValue = formData[fieldID];
        if (Array.isArray(currentValue)) currentValue = currentValue[0] || "";

        // NEW: Define custom renderOption specifically for the 'segment' field
        let renderCustomOption = null;
        if (fieldID === "segment") {
            renderCustomOption = (option, isSelected) => (
                <div className="flex items-center gap-2">
                    {/* Display the colored dot using the 'color' property from the option */}
                    <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: option.color }}
                    />
                    {/* Display the segment name (label) */}
                    <span className={(isSelected ? "font-semibold" : "font-normal", "flex-1 min-w-0 truncate")}>
                        {option.label}
                    </span>
                </div>
            );
        }
        return (
            <div key={fieldID} className={conFormClasses.spaceY}>
                <Label required={isFieldRequired(fieldID)}>{label}</Label>
                <CustomSearchableDropdown
                    options={finalOptions}
                    value={currentValue}
                    onSelect={(val, option, searchText) => {  // Add searchText parameter here
                        if (val === "add" && allowAdd) {
                            const fieldToMasterTypeMap = {
                                'city': 'cities',
                                'source': 'sources',
                                'segment': 'segments',
                                'nextTaskTyp': 'recordTypes',
                                'recordType': 'recordTypes',
                                'regionID': 'regions'
                            };

                            const masterType = fieldToMasterTypeMap[fieldID];
                            if (masterType && onOpenMasterDialog) {
                                // Pass the searchText as the third parameter
                                onOpenMasterDialog(masterType, fieldID, searchText);
                            }
                        } else {
                            if (fieldID === 'pocUserID' && !Array.isArray(val)) {
                                onSelectChange(fieldID, [val.toString()]);
                            } else {
                            onSelectChange(fieldID, val);
                        }
                        }
                    }}
                    label={label}
                    placeholder={placeholder}
                    searchPlaceholder={searchPlaceholder}
                    displayKey={displayKey}
                    valueKey={valueKey}
                    allowAdd={allowAdd}
                    addText={addText}
                    emptyMessage={emptyMessage}
                    disabled={finalDisabled}
                    searchEnabled={searchEnabled}
                    renderOption={renderCustomOption}
                    changeType={changeType}
                    searchEnabledLength={searchEnabledLength !== undefined && searchEnabledLength !== null ? searchEnabledLength : 10}
                />
            </div>
        );
    };

    // Main form field renderer with all field types
    const renderFormField = (fieldID, label, placeholder, type = "text", required = false, disabled = false) => {
        // Smart Seller Mode: Hide Executive/Assigned To fields
        if (isSmartSellerMode() && (fieldID === 'pocUserID' || fieldID === 'nextTaskPocUserID' || fieldID === 'interactionBy')) {
            return null;
        }

        // Check if field should be disabled due to ConnEdit permission
        const permissionDisabled = isFieldDisabledByPermission(fieldID);
        const finalDisabled = disabled || permissionDisabled;
        const isExtraField = masterData.extraFields && masterData.extraFields.some(fieldName =>
            fieldName.toLowerCase().replace(/\s+/g, '') === fieldID
        );

        // Define fields shown in step 1 (Create Mode)
        const BASIC_FIELDS_ONLY = ["businessName", "name", "mobile"];
        // Only allow email in Step 1 if it's Desktop
        if (!isMobile) BASIC_FIELDS_ONLY.push("email");
        const isCreateMode = !connID; // If no ID passed, we are in Step 1

        const isOnlySupplier = formData.connType === "Supplier";
        const moreDetailsFields = ["city", "state", "country", "gstin", "source", "since", "segment", "businessPotential", "tags", "notes", "pocUserID", "email", "regionID"];
        const nextTaskFields = ["nextTaskDT", "nextTaskTime", "nextTaskTyp", "nextTaskNotes", "nextTaskPocUserID"];
        const lastInteractionFields = ["recordDate", "recordType", "recordTime", "interactionBy", "recordNotes"];
        const shouldShowMoreDetails = !connID && uiStates.showMoreDetails && moreDetailsFields.includes(fieldID) && !(isOnlySupplier && (fieldID === "segment" || fieldID === "businessPotential"));
        // Check if field is enabled in Redux preferences
        // Always show Next Task fields if we are in Step 2 (showMoreDetails is true) or Edit Mode
        const isNextTaskField = ["nextTaskDT", "nextTaskTime", "nextTaskTyp", "nextTaskNotes", "nextTaskPocUserID"].includes(fieldID);
        const forceShowNextTask = isNextTaskField && (uiStates.showMoreDetails || connID);

        const isPrefEnabled =
            (fieldID === "city" && prefsEnabled.city) ||
            (fieldID === "state" && prefsEnabled.state) ||
            (fieldID === "country" && prefsEnabled.country) ||
            (fieldID === "gstin" && prefsEnabled.gstin) ||
            (fieldID === "businessPotential" && prefsEnabled.potential) ||
            (fieldID === "source" && prefsEnabled.source) ||
            (fieldID === "since" && prefsEnabled.since) ||
            (fieldID === "segment" && prefsEnabled.segment) ||
            (fieldID === "pocUserID" && prefsEnabled.poc) ||
            (fieldID === "nextTaskDT" && (prefsEnabled.nextTask || forceShowNextTask)) ||
            (fieldID === "nextTaskTime" && (prefsEnabled.nextTask || forceShowNextTask)) ||
            (fieldID === "nextTaskTyp" && (prefsEnabled.nextTask || forceShowNextTask)) ||
            (fieldID === "nextTaskNotes" && (prefsEnabled.nextTask || forceShowNextTask)) ||
            (fieldID === "nextTaskPocUserID" && (prefsEnabled.nextTask || forceShowNextTask)) ||
            (fieldID === "notes" && prefsEnabled.notes) ||
            (fieldID === "tags" && prefsEnabled.tags) ||
            (fieldID === "email" && (prefsEnabled.email || forceShowEmail)) ||
            (fieldID === "regionID" && masterData?.regions?.length > 0 && connViewTypeID != 5 && prefsEnabled.region);

        // Step 1: Restrict fields
        if (!uiStates.showMoreDetails) {
            // Allow product field in Step 1 if it's a Desktop Inquiry
            if (!BASIC_FIELDS_ONLY.includes(fieldID) && !(isDesktopInquiry && fieldID === 'product')) return null;
        }

        // Step 2: Hide Basic Fields if expanded (User Request) - Keep visible in Edit Mode
        if (isStepTwo || (uiStates.showMoreDetails && !connID)) {
            if (BASIC_FIELDS_ONLY.includes(fieldID)) return null;
        }
        const isSupplierOrOther = formData.connType && (
            formData.connType.split(',').map(t => t.trim().toLowerCase()).includes('supplier') ||
            formData.connType.split(',').map(t => t.trim().toLowerCase()).includes('other') ||
            formData.connType.toLowerCase() === 's' ||
            formData.connType.toLowerCase() === 'o'
        );

        if (isSupplierOrOther && (fieldID === "segment" || fieldID === "businessPotential")) {
            return null;
        }

        const shouldShow =
            ["businessName", "name", "mobile", "connType", "product"].includes(fieldID) ||
            isPrefEnabled ||
            (fieldID.startsWith("customfield")) || // Assuming extra fields are custom
            isExtraField ||
            (!connID && lastInteractionFields.includes(fieldID));


        if (!shouldShow) return null;
        if (fieldID === 'email' && hideFields.email) {
            return null;
        }
        const maxLengthMap = {
            businessName: 100,
            email: 50,
            gstin: 15,
        };

        switch (type) {
            case "textarea": {
                let maxLengthLimit, minHeight;

                if (label === "Notes" || fieldID === "notes") {
                    maxLengthLimit = 4000;
                    minHeight = "min-h-[60px]";
                } else if (label === "Detailed Description") {
                    maxLengthLimit = 8000;
                    minHeight = "min-h-[120px]";
                } else if (label === "recordNotes" || label === "nextTaskNotes") {
                    maxLengthLimit = 1000;
                    minHeight = "min-h-[60px]";
                } else {
                    maxLengthLimit = 200;
                    minHeight = "min-h-[60px]";
                }

                if (!isMobile && (fieldID === "notes" || label === "Notes")) {
                    return (
                        <div key={fieldID} className={conFormClasses.spaceY}>
                            <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>{label}</Label>
                            <Input
                                id={fieldID}
                                name={fieldID}
                                value={formData[fieldID] || ""}
                                onChange={onInputChange}
                                placeholder={placeholder}
                                autoComplete="off"
                                maxLength={maxLengthLimit}
                                className="w-full text-sm h-11"
                                disabled={finalDisabled}
                                type="text"
                            />
                        </div>
                    );
                }

                return (
                    <div key={fieldID} className={conFormClasses.spaceY}>
                        <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>{label}</Label>
                        <Textarea
                            id={fieldID}
                            name={fieldID}
                            value={formData[fieldID] || ""}
                            onChange={onInputChange}
                            placeholder={placeholder}
                            autoComplete="off"
                            maxLength={maxLengthLimit}
                            className={`${minHeight} w-full resize-none text-sm`}
                            disabled={finalDisabled}
                        />
                        {label === "Detailed Description" && (
                            <div className="text-xs text-gray-500 text-right">
                                {(formData[fieldID] || "").length}/{maxLengthLimit} characters
                            </div>
                        )}
                    </div>
                );
            }

            case "text":
                if (fieldID === "tags") {
                    return (
                        <TagsInput
                            key={fieldID}
                            fieldID={fieldID}
                            label={label}
                            value={formData[fieldID] || ''}
                            onChange={(newValue) => onInputChange({ target: { name: 'tags', value: newValue } })}
                            placeholder={placeholder}
                            required={isFieldRequired(fieldID)}
                            disabled={finalDisabled}
                        />
                    );
                }
                if (fieldID === "name") {
                    return (
                        <NameInput
                            key={fieldID}
                            fieldID={fieldID}
                            label={label}
                            placeholder={placeholder}
                            value={formData[fieldID]}
                            onChange={onInputChange}
                            onContactPickerClick={onContactPickerClick}
                            required={isFieldRequired(fieldID)}
                            maxLength={maxLengthMap[fieldID]}
                            showContactPicker={false}
                            className={conFormClasses.spaceY}
                            disabled={finalDisabled}
                        />
                    );
                }

                if (fieldID === "businessName") {
                    return (
                        <NameInput
                            key={fieldID}
                            fieldID={fieldID}
                            label={label}
                            placeholder={placeholder}
                            value={formData[fieldID]}
                            onChange={onInputChange}
                            onContactPickerClick={onContactPickerClick}
                            required={isFieldRequired(fieldID)}
                            maxLength={maxLengthMap[fieldID]}
                            showContactPicker={isMobileDevice()}
                            className={conFormClasses.spaceY}
                            disabled={finalDisabled}
                        />
                    );
                }

                return (
                    <div key={fieldID} className={conFormClasses.spaceY}>
                        <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>{label}</Label>
                        <Input
                            id={fieldID}
                            name={fieldID}
                            value={formData[fieldID] || ""}
                            onChange={onInputChange}
                            placeholder={placeholder}
                            autoComplete="off"
                            type="text"
                            maxLength={maxLengthMap[fieldID]}
                            disabled={finalDisabled}
                        />
                    </div>
                );

            case "mobile":
                return (
                    <MobileInput
                        key={fieldID}
                        fieldID={fieldID}
                        label={label}
                        placeholder={placeholder}
                        value={formData[fieldID]}
                        onChange={onInputChange}
                        selectedCountryCode={uiStates.selectedCountryCode}
                        onCountryCodeChange={(newCountryCode) => {
                            setUIStates(prev => ({ ...prev, selectedCountryCode: newCountryCode }));
                            const newCountry = countries.find(c => c.CC === newCountryCode);
                            if (newCountry) {
                                onInputChange({
                                    target: {
                                        name: 'country',
                                        value: newCountry.Country
                                    }
                                });
                                if (newCountry.Country !== "India") {
                                    onInputChange({
                                        target: {
                                            name: 'state',
                                            value: ""
                                        }
                                    });
                                }
                            }
                        }}
                        className={conFormClasses.spaceY}
                        disabled={finalDisabled}

                    />
                );

            case "number":
                return (
                    <BusinessInput
                        key={fieldID}
                        fieldID={fieldID}
                        label={label}
                        placeholder={placeholder}
                        value={formData[fieldID]}
                        onChange={onInputChange}
                        required={isFieldRequired(fieldID)}
                        maxLength="12"
                        maxDecimalPlaces={2}
                        maxIntegerDigits={9}
                        className={conFormClasses.spaceY}
                        disabled={finalDisabled}
                    />
                );

            case "stateSelect": {
                const isIndiaSelected = formData.country === "India";
                if (isIndiaSelected) {
                    // Use searchable dropdown for India
                    const config = searchableDropdownConfigs[type];
                    if (config) {
                        const options = config.dataSource.map(config.mapper);
                        return renderSearchableDropdownWithDisabled(fieldID, label, placeholder, options, { ...config, disabled });
                    }
                } else {
                    // Use text input for non-India countries
                    return (
                        <div key={fieldID} className={conFormClasses.spaceY}>
                            <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>{label}</Label>
                            <Input
                                id={fieldID}
                                name={fieldID}
                                value={formData[fieldID]}
                                onChange={onInputChange}
                                placeholder="e.g. Gujarat"
                                autoComplete="off"
                                type="text"
                                maxLength={50}
                                disabled={finalDisabled}
                            />
                        </div>
                    );
                }
                return null;
            }

            case "productSelect":
                return (
                    <div key={fieldID} className={conFormClasses.spaceY}>
                        <Label required>Offering Category</Label>
                        <CustomSearchableDropdown
                            options={masterData.products.map(p => ({ value: p.name || p.PNm, label: p.name || p.PNm }))}
                            value={inquiryData?.product}
                            onSelect={(val, option, searchText) => {
                                if (val === 'add') {
                                    if (onOpenMasterDialog) {
                                        onOpenMasterDialog('products', 'product', searchText);
                                    }
                                } else {
                                    onInquiryChange('product', val);
                                    onInquiryChange('model', '');
                                }
                            }}
                            placeholder="Select Offering"
                            searchPlaceholder="Search Offering"
                            displayKey="label"
                            valueKey="value"
                            allowAdd={true}
                            addText="Add Offering"
                            emptyMessage="No offerings found"
                            searchEnabled={true}
                        />
                    </div>
                );
            case "executiveSelect":
                if (fieldID === "pocUserID" && connID && !isStepTwo) {
                    // Import at top of file
                    // import ExecutiveTagsInput from '../../Components/Common/ExecutiveTagsInput';

                    // Identify users who must be fixed (Admin, Full Access, All Contacts Access)
                    const fixedUsers = masterData.users.filter(u => u.isAdmin || u.fullAccess || u.connViewTypeID === 1);
                    const fixedIDs = fixedUsers.map(u => u.userID.toString());

                    // Ensure fixed users are included in selected IDs
                    const currentSelected = Array.isArray(formData[fieldID]) ? formData[fieldID] :
                        (formData[fieldID] ? [formData[fieldID].toString()] : []);
                    const effectiveSelectedIDs = [...new Set([...currentSelected, ...fixedIDs])].map(id => id.toString());

                    // Prepare ALL users as options (fixed + type 4)
                    const allUserOptions = masterData.users
                        .filter(u => u.connViewTypeID === 4 || fixedIDs.includes(u.userID.toString()))
                        .map(u => ({
                            value: u.userID.toString(),
                            label: u.name,
                            fixed: (u.isAdmin || u.fullAccess || u.connViewTypeID === 1),
                            hiddenFromList: (u.isAdmin || u.fullAccess || u.connViewTypeID === 1)
                        }));

                    return (
                        <ExecutiveTagsInput
                            key={fieldID}
                            fieldID={fieldID}
                            label={label}
                            value={effectiveSelectedIDs}
                            onChange={(newValue) => {
                                // Filter out fixed users so only manually selectable users (Type 4) are saved
                                const nonFixedValues = newValue.filter(val => !fixedIDs.includes(val.toString()));
                                onSelectChange(fieldID, nonFixedValues);
                            }}
                            options={allUserOptions}
                            placeholder={placeholder}
                            required={isFieldRequired(fieldID)}
                            disabled={finalDisabled}
                            displayKey="label"
                            valueKey="value"
                        />
                    );
                }
            // Fallthrough for nextTaskPocUserID which remains single select
            case "citySelect":
            case "sourceSelect":
            case "segmentSelect":
            case "recordTypeSelect": // Remove "taskTypeselect" from here
            case "taskTypeSelect":
            case "regionSelect":
            // case "executiveSelect":
            case "countrySelect":
                {
                    // For executiveSelect falling through here (e.g. nextTaskPocUserID), we use the config
                    const configType = type === "executiveSelect" ? "executiveSelect" : type;
                    const config = searchableDropdownConfigs[configType];
                    if (config) {
                        const options = config.dataSource.map(config.mapper);
                        return renderSearchableDropdownWithDisabled(fieldID, label, placeholder, options, { ...config, disabled: finalDisabled });
                    }
                    return null;
                }

            case "date": {
                const dateValue = formData[fieldID] ? new Date(formData[fieldID]) : undefined;
                const isDatePickerOpen = uiStates.datePickerStates[fieldID] || false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const currentYear = today.getFullYear();
                let minDate, maxDate, defaultMonth, param, fromYear, toYear;

                if (fieldID === "since" || fieldID === "recordDate") {
                    minDate = null;
                    maxDate = today;
                    param = "noFuture";
                    fromYear = 1900;
                    toYear = currentYear;
                    defaultMonth = dateValue || today;
                } else if (fieldID === "nextTaskDT") {
                    minDate = today;
                    maxDate = null;
                    param = "";
                    fromYear = currentYear - 5;
                    toYear = currentYear + 25;
                    // Default to tomorrow if no value selected
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    defaultMonth = dateValue || tomorrow;
                } else {
                    minDate = null;
                    maxDate = null;
                    param = "";
                    fromYear = currentYear - 10;
                    toYear = currentYear + 10;
                    defaultMonth = dateValue || new Date();
                }

                return (
                    <DatePicker
                        key={fieldID}
                        fieldID={fieldID}
                        value={dateValue}
                        onChange={(date) => onDateChange(fieldID, date)}
                        placeholder="Select Date"
                        label={label}
                        required={isFieldRequired(fieldID)}
                        className={`${conFormClasses.spaceY} ${label === "Last Interaction" ? "font-semibold" : ""}`}
                        disabled={finalDisabled || isFieldDisabled(fieldID)}
                        minDate={minDate}
                        maxDate={maxDate}
                        fromYear={fromYear}
                        toYear={toYear}
                        defaultMonth={defaultMonth}
                        param={param}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                setUIStates(prev => ({
                                    ...prev,
                                    datePickerStates: { ...prev.datePickerStates, [fieldID]: false }
                                }));
                            }
                        }}
                        onOpenChange={(open) => setUIStates(prev => ({
                            ...prev,
                            datePickerStates: { ...prev.datePickerStates, [fieldID]: open }
                        }))}
                        open={isDatePickerOpen}
                    />
                );
            }

            case "time": {
                const isToday = formData[fieldID === "recordTime" ? "recordDate" : "nextTaskDT"];
                const selectedDate = isToday ? new Date(formData[fieldID === "recordTime" ? "recordDate" : "nextTaskDT"]) : null;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isSelectedDateToday = selectedDate && selectedDate.setHours(0, 0, 0, 0) === today.getTime();
                let minTime, maxTime;

                if (fieldID === "recordTime" && isSelectedDateToday) {
                    maxTime = new Date().toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                } else if (fieldID === "nextTaskTime" && isSelectedDateToday) {
                    minTime = new Date().toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                }

                return (
                    <TimeInputWithDialog
                        key={fieldID}
                        fieldID={fieldID}
                        value={formData[fieldID]}
                        onChange={(timeString) => onTimeChange(fieldID, timeString)}
                        placeholder={placeholder}
                        required={required}
                        disabled={finalDisabled || isFieldDisabled(fieldID)}
                        minTime={minTime}
                        maxTime={maxTime}
                        label={label}
                    />
                );
            }

            default:
                return (
                    <div key={fieldID} className={conFormClasses.spaceY}>
                        <Label htmlFor={fieldID} required={isFieldRequired(fieldID)}>{label}</Label>
                        <Input
                            id={fieldID}
                            name={fieldID}
                            value={formData[fieldID]}
                            onChange={onInputChange}
                            placeholder={placeholder}
                            autoComplete="off"
                            type={type}
                            maxLength={fieldID === "gstin" ? 15 : 1000}
                            disabled={finalDisabled}
                        />
                    </div>
                );
        }
    };

    // Render Inquiry Section
    const renderInquirySection = (isMR = false, isShowLabel = true) => {
        if (!isInquiryChecked || connID) return null; // Don't show in edit mode

        // Determine Product Logic (Is Product vs Service, has Models/Packages)
        const selectedProduct = masterData.products.find(p => (p.name || p.PNm) === inquiryData?.product);
        // Only show quantity if a product is selected AND it is of type Product (PTID=1)
        const showQuantity = inquiryData?.product && ((selectedProduct?.PTID || selectedProduct?.offeringType || 1) === 1);

        let modelOptions = [];
        if (selectedProduct) {
            try {
                const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
                if (modelsData) {
                    const parsed = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                    if (Array.isArray(parsed)) modelOptions = parsed.map(m => ({ value: m.mNm || m.MNm, label: m.mNm || m.MNm }));
                }
            } catch (e) { console.error(e); }
        }
        const hasModels = modelOptions.length > 0;
        const offeringLabel = (selectedProduct?.PTID || selectedProduct?.offeringType || 1) === 1 ? 'Model' : 'Package';

        // Thread Custom Fields Logic
        const currentThreadTypeID = threadTypeID || 1;
        const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === currentThreadTypeID);
        let threadCustomFields = [];
        if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
            try {
                threadCustomFields = JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD);
            } catch (e) { threadCustomFields = []; }
        }

        return (
            <div className={`${isMR ? '' : 'mt-4'}`}>
                {/* {isShowLabel &&
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-gray-900">Inquiry Details</h3>
                    </div>} */}
                <div className={isMobile ? "space-y-4" : (!connID && !isStepTwo) ? "grid grid-cols-1 sm:grid-cols-2 gap-4" : conFormClasses.gridResponsive}>
                    {/* Product / Service */}
                    {!isDesktopInquiry && (
                        <div className={isMobile ? "w-full" : conFormClasses.colSpan1}>
                            <div className={conFormClasses.spaceY}>
                                <Label required>Offering Category</Label>
                                {(!connID && calledFrom === 'FORCE_INQUIRY' && connectionData?.length === 0) ? (
                                    <Input
                                        value={inquiryData?.product || ""}
                                        onChange={(e) => onInquiryChange('product', e.target.value)}
                                        placeholder="Enter Offering"
                                    />
                                ) : (
                                    <CustomSearchableDropdown
                                        options={masterData.products.map(p => ({ value: p.name || p.PNm, label: p.name || p.PNm }))}
                                        value={inquiryData?.product}
                                        onSelect={(val, option, searchText) => {
                                            if (val === 'add') {
                                                if (onOpenMasterDialog) {
                                                    onOpenMasterDialog('products', 'product', searchText);
                                                }
                                            } else {
                                                onInquiryChange('product', val);
                                                onInquiryChange('model', ''); // Reset model
                                            }
                                        }}
                                        placeholder="Select Offering"
                                        searchPlaceholder="Search Offering"
                                        displayKey="label"
                                        valueKey="value"
                                        allowAdd={true}
                                        addText="Add Offering"
                                        emptyMessage="No offerings found"
                                        searchEnabled={true}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Model / Package Dropdown (Conditional) */}
                    {hasModels && (
                        <div className={isMobile ? "w-full" : conFormClasses.colSpan1}>
                            <div className={conFormClasses.spaceY}>
                                <Label>{offeringLabel}</Label>
                                <CustomSearchableDropdown
                                    options={modelOptions}
                                    value={inquiryData?.model}
                                    onSelect={(val) => onInquiryChange('model', val)}
                                    placeholder={`Select ${offeringLabel}`}
                                    searchPlaceholder={`Search ${offeringLabel}`}
                                    displayKey="label"
                                    valueKey="value"
                                    allowAdd={false}
                                    emptyMessage={`No ${offeringLabel.toLowerCase()}s found`}
                                    searchEnabled={true}
                                />
                            </div>
                        </div>
                    )}

                    {/* Reference and Thread Custom Fields removed from Step 1 */}

                    {/* Business Value Layout Logic */}
                    {showQuantity ? (
                        <>
                            {/* Case 1: Show Quantity -> [Biz Value | Quantity] then [Due Date] */}
                            <div className={`${isMobile ? "w-full" : conFormClasses.colSpan2} grid grid-cols-2 gap-2`}>
                                <div className="w-full">
                                    <BusinessInput
                                        label="Business Value"
                                        placeholder={`e.g. 12,500`}
                                        value={inquiryData?.businessValue}
                                        onChange={(e) => onInquiryChange('businessValue', e.target.value)}
                                        required={false}
                                        maxDecimalPlaces={2}
                                        currencySymbol={currencySymbol}
                                        className={conFormClasses.spaceY}
                                    />
                                </div>
                                <div className="w-full">
                                    <div className={conFormClasses.spaceY}>
                                        <Label>Quantity</Label>
                                        <Input
                                            value={inquiryData?.quantity || ""}
                                            onChange={(e) => onInquiryChange('quantity', e.target.value)}
                                            placeholder="e.g. 20 no.s"
                                            maxLength={50}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={isMobile ? "grid grid-cols-2 gap-2" : conFormClasses.colSpan1}>
                                <DatePicker
                                    value={inquiryData?.dueDT ? new Date(inquiryData.dueDT) : undefined}
                                    onChange={(date) => onInquiryChange('dueDT', date ? format(date, "dd-MMM-yy") : "")}
                                    placeholder="Select Date"
                                    label="Tgt / Close by"
                                    required={true}
                                    className={conFormClasses.spaceY}
                                    minDate={new Date()}
                                />
                            </div>
                        </>
                    ) : (
                        /* Case 2: No Quantity -> [Biz Value | Due Date] side-by-side */
                        <div className={`${isMobile ? "w-full" : conFormClasses.colSpan2} grid grid-cols-2 gap-2`}>
                            <div className="w-full">
                                <BusinessInput
                                    label="Business Value"
                                    placeholder={`e.g. 12,500`}
                                    value={inquiryData?.businessValue}
                                    onChange={(e) => onInquiryChange('businessValue', e.target.value)}
                                    required={false}
                                    maxDecimalPlaces={2}
                                    currencySymbol={currencySymbol}
                                    className={conFormClasses.spaceY}
                                />
                            </div>
                            <div className="w-full">
                                <DatePicker
                                    value={inquiryData?.dueDT ? new Date(inquiryData.dueDT) : undefined}
                                    onChange={(date) => onInquiryChange('dueDT', date ? format(date, "dd-MMM-yy") : "")}
                                    placeholder="Select Date"
                                    label="Tgt / Close by"
                                    required={true}
                                    className={conFormClasses.spaceY}
                                    minDate={new Date()}
                                />
                            </div>
                        </div>
                    )}

                    {/* Inquiry Stage / Status */}
                    {inquiryStages && inquiryStages.length > 0 && (
                        <div className={isMobile ? "w-full" : conFormClasses.colSpan1}>
                            <div className={conFormClasses.spaceY}>
                                <Label>Status</Label>
                                <Select
                                    value={inquiryData?.stageID ? String(inquiryData.stageID) : ""}
                                    onValueChange={(val) => onInquiryChange('stageID', val ? Number(val) : null)}
                                >
                                    <SelectTrigger className="w-full">
                                        {inquiryData?.stageID ?
                                            inquiryStages.find(s => s.SID === inquiryData.stageID)?.S || "Select Stage"
                                            : "Select Stage"
                                        }
                                    </SelectTrigger>
                                    <SelectContent>
                                        {inquiryStages.map((stage) => (
                                            <SelectItem key={stage.SID} value={String(stage.SID)}>
                                                {stage.S}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    <div className={isMobile ? "w-full" : conFormClasses.colSpan1}>
                        <div className={conFormClasses.spaceY}>
                            <Label>Notes</Label>
                            <div className="relative">
                                <Input
                                    id="inquiry-notes-input"
                                    value={inquiryData?.notes || ""}
                                    onChange={(e) => onInquiryChange('notes', e.target.value)}
                                    placeholder="e.g. Interested in HP computers"
                                    className="pr-20"
                                />
                                <SpeechControls
                                    targetId="inquiry-notes-input"
                                    value={inquiryData?.notes || ""}
                                    onChange={(val) => onInquiryChange('notes', val)}
                                    isMobile={isMobile}
                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Attachment Section
    const renderAttachmentSection = () => {
        if (!SHOW_CONTACT_CARD) return null;

        const fileInputId = "contact-attachment-file";

        const handleFileChange = (e) => {
            const file = e.target.files[0];
            if (file) {
                setSelectedFileForCrop(file);
            }
        };

        const handleClearAttachment = (e) => {
            e.stopPropagation();
            onInputChange({
                target: {
                    name: 'attachment',
                    value: ''
                }
            });
            onInputChange({
                target: {
                    name: 'attachmentName',
                    value: ''
                }
            });
            // Reset input file value
            const fileInput = document.getElementById(fileInputId);
            if (fileInput) fileInput.value = "";
        };

        // Create object URL for preview
        let previewUrl = null;
        if (formData.attachment) {
            if (formData.attachment instanceof File) {
                previewUrl = URL.createObjectURL(formData.attachment);
            } else {
                previewUrl = getUploadUrl(`Uploads/${formData.attachment}`);
            }
        }

        return (
            <div className="w-full pt-1" id="business-card-section">
                {/* Hidden File Input */}
                <input
                    type="file"
                    id={fileInputId}
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                />

                {!formData.attachment ? (
                    <div className="flex items-center">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground px-0 hover:text-primary flex items-center gap-1.5 focus:outline-none"
                            onClick={() => document.getElementById(fileInputId).click()}
                        >
                            <Plus className="h-4 w-4" />
                            <span className="underline underline-offset-4">Add Business Card</span>
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-1.5 w-full sm:w-1/2">
                        <Label htmlFor="attachment">Business Card</Label>
                        <div className="relative flex items-center justify-between h-11 border border-gray-300 bg-white rounded-md px-2 py-2 text-sm">
                            <div className="flex items-center gap-2 truncate w-full">
                                {previewUrl && (
                                    <img
                                        src={previewUrl}
                                        alt="Business Card Preview"
                                        className="h-8 w-8 object-cover rounded border border-gray-100 flex-shrink-0 cursor-zoom-in"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsImageLoading(true);
                                            setIsPreviewOpen(true);
                                        }}
                                    />
                                )}
                                <span className="truncate text-sm text-gray-700 font-medium">
                                    {formData.attachment instanceof File ? formData.attachment.name : (formData.attachmentName || "Business Card")}
                                </span>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 focus:outline-none"
                                onClick={handleClearAttachment}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Full-view Modal Overlay using nested Dialog to bypass Radix focus/pointer traps */}
                <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                    <DialogContent className="max-w-[100vw] h-screen max-h-screen bg-black/50 p-0 border-0 flex items-center justify-center z-[10000]">
                        {/* Close Button */}
                        <button
                            type="button"
                            className="absolute top-6 right-6 text-white hover:text-gray-300 p-2 rounded-full transition-colors focus:outline-none z-[10001] cursor-pointer bg-black/40 hover:bg-black/60 flex items-center justify-center"
                            onClick={() => setIsPreviewOpen(false)}
                        >
                            <X className="h-6 w-6" />
                        </button>

                        {/* Spinner */}
                        {isImageLoading && (
                            <div className="absolute flex flex-col items-center gap-2 z-[10002]">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
                                <span className="text-xs text-white/70">Loading image...</span>
                            </div>
                        )}

                        <div 
                            className="w-full h-full flex items-center justify-center p-4 cursor-pointer"
                            onClick={() => setIsPreviewOpen(false)}
                        >
                            <img
                                src={previewUrl}
                                alt="Business Card Full Preview"
                                className={`max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${
                                    isImageLoading ? 'opacity-0 h-0 w-0' : 'opacity-100'
                                }`}
                                onLoad={() => setIsImageLoading(false)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Image Cropper Dialog */}
                {selectedFileForCrop && (
                    <ImageCropperDialog
                        isOpen={Boolean(selectedFileForCrop)}
                        file={selectedFileForCrop}
                        onClose={() => {
                            setSelectedFileForCrop(null);
                            const fileInput = document.getElementById(fileInputId);
                            if (fileInput) fileInput.value = "";
                        }}
                        onCropComplete={(croppedFile) => {
                            onInputChange({
                                target: {
                                    name: 'attachment',
                                    value: croppedFile
                                }
                            });
                            onInputChange({
                                target: {
                                    name: 'attachmentName',
                                    value: croppedFile.name
                                }
                            });
                            setSelectedFileForCrop(null);
                        }}
                    />
                )}
            </div>
        );
    };

    // Render form layout with all sections
    const renderDynamicFields = () => {
        // Show "Contact Details" header if Inquiry section is active or forced, to distinguish sections
        const showContactHeader = calledFrom === 'FORCE_INQUIRY' || isInquiryChecked;

        const basicFields = connID ? [
            { id: "businessName", label: "Business Name", placeholder: "e.g. John Infrastructure", type: "text" }
        ] : [
            { id: "businessName", label: "Business Name", placeholder: "e.g. John Infrastructure", type: "text" },
            { id: "name", label: "Contact Person", placeholder: "e.g. John Doe", type: "text", colSpan: conFormClasses.colSpan1 },
            { id: "designation", label: "Designation", placeholder: "e.g. Manager", type: "text", colSpan: conFormClasses.colSpan1 },
            { id: "mobile", label: "Mobile", placeholder: "9876543210", type: "mobile", colSpan: conFormClasses.colSpan1 },
            { id: "email", label: "Email", placeholder: "e.g. john.doe@gmail.com", type: "email", colSpan: conFormClasses.colSpan1 },
            ...(isDesktopInquiry ? [{ id: "product", label: "Offering Category", placeholder: "Select Offering", type: "productSelect", colSpan: conFormClasses.colSpan1 }] : [])
        ];

        const moreDetailsFields = [
            [
                { id: "city", label: "City", placeholder: "e.g. Mumbai", type: "citySelect" },
                { id: "state", label: "State", placeholder: "Select State", type: "stateSelect" },
                { id: "country", label: "Country", placeholder: "Select Country", type: "countrySelect" },
                { id: "gstin", label: taxIDLabel, placeholder: "e.g. 22AAAAA0000A1Z5", type: "text" }
            ],
            [
                { id: "source", label: "Source", placeholder: "e.g. Referral", type: "sourceSelect" },
                { id: "since", label: "Since", placeholder: "Select Date", type: "date" },
                { id: "segment", label: "Category", placeholder: "e.g. Enterprise", type: "segmentSelect" },
                { id: "businessPotential", label: "Business Potential", placeholder: `e.g. ${currencySymbol} 10,500`, type: "number" }
            ],
            [
                { id: "tags", label: "Tags", placeholder: "Enter tag and press Enter", type: "text", colSpan: "sm:col-span-2" },
                { id: "regionID", label: "Region", placeholder: "e.g. North-Gujarat", type: "regionSelect", colSpan: "sm:col-span-2" }
            ],
            [
                { id: "pocUserID", label: (connID && !isStepTwo) ? "Executives" : "Executive", placeholder: "Select Executive", type: "executiveSelect", colSpan: (connID && !isStepTwo) ? "sm:col-span-2" : "sm:col-span-1" },
                { id: "notes", label: "Notes", placeholder: "e.g. Interested in project HP Laptop.", type: "textarea", colSpan: (connID && !isStepTwo) ? "sm:col-span-2" : "sm:col-span-3" }
            ]
        ];

        if (isMobile) {
            return (
                <div className="space-y-4">
                    {/* Optional Header for Contact Section on Mobile
                    {showContactHeader && (
                        <div className="">
                            <h3 className="text-sm font-bold text-gray-900">Contact Details</h3>
                        </div>
                    )} */}

                    {/* Mobile Layout */}

                    {/* Connection Type - Full Width (Moved to Top) */}
                    {editMode !== 'CONTACTS_ONLY' && (![2, 3].includes(connViewTypeID) && !hideFields.connType && !isSmartSellerMode() && !isStepTwo && (connID || !uiStates.showMoreDetails)) &&
                        (<div className={conFormClasses.spaceY}>
                            {/* <Label className="text-sm font-medium" required={isFieldRequired('connType')}>Connection Type</Label> */}
                            <div className={`${conFormClasses.flexGap} !pt-0`}>
                                {connTypes.map((option) => (
                                    <div key={option.value} className="flex items-center">
                                        <input
                                            type={isInqOnlyMode ? 'radio' : 'checkbox'}
                                            name={isInqOnlyMode ? 'connectionType' : option.value}
                                            id={`connectionType-${option.value}`}
                                            checked={isConnectionTypeSelected(option.value)}
                                            onChange={(e) => onConnectionTypeChange(option.value, e.target.checked)}
                                            disabled={isFieldDisabledByPermission('connType') || (isInqOnlyMode && disableConnType)}
                                            className={conFormClasses.checkbox}
                                        />
                                        <Label
                                            htmlFor={`connectionType-${option.value}`}
                                            className={conFormClasses.label}
                                        >
                                            {option.label}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>)
                    }

                    {/* Business Name - Full Width - Hide in Step 2 */}
                    {editMode !== 'CONTACTS_ONLY' && (!isStepTwo && (connID || !uiStates.showMoreDetails)) && (
                        <div className="w-full">
                            {renderFormField(basicFields[0].id, basicFields[0].label, basicFields[0].placeholder, basicFields[0].type, true)}
                        </div>
                    )}

                    {editMode !== 'CONTACTS_ONLY' && !connID && (
                        <div className="space-y-3">
                            {/* Name - Full Width */}
                            {(() => {
                                const nameField = basicFields.find(f => f.id === 'name');
                                return (
                                    <div className="w-full">
                                        {renderFormField(nameField.id, nameField.label, nameField.placeholder, nameField.type, true)}
                                    </div>
                                );
                            })()}

                            {/* Mobile - ~70% Width (Grid col 3, span 2) */}
                            <div className="grid grid-cols-3 gap-3">
                                {(() => {
                                    const mobileField = basicFields.find(f => f.id === 'mobile');
                                    return (
                                        <div className="col-span-2">
                                            {renderFormField(mobileField.id, mobileField.label, mobileField.placeholder, mobileField.type, true)}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Render remaining basic fields (Region, etc) */}
                            {basicFields.slice(1).filter(f => f.id !== 'email' && f.id !== 'name' && f.id !== 'mobile').map(field => {
                                const fieldElement = renderFormField(field.id, field.label, field.placeholder, field.type, true);
                                return fieldElement ? (
                                    <div key={field.id} className="w-full">
                                        {fieldElement}
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}

                    {/* More Details Section */}
                    {/* More Details Section */}
                    {editMode !== 'CONTACTS_ONLY' && (() => {
                        const hasAnyVisibleField =
                            (renderFormField("email", "Email", "e.g. john.doe@gmail.com", "email", false)) ||
                            renderFormField("city", "City", "e.g. Mumbai", "citySelect", false) ||
                            renderFormField("state", "State", "Select State", "stateSelect", false) ||
                            renderFormField("country", "Country", "Select Country", "countrySelect", false) ||
                            renderFormField("gstin", taxIDLabel, "e.g. 22AAAAA0000A1Z5", "text", false) ||
                            renderFormField("source", "Source", "e.g. Referral", "sourceSelect", false) ||
                            renderFormField("since", "Since", "Select Date", "date", false) ||
                            renderFormField("segment", "Category", "e.g. Enterprise", "segmentSelect", false) ||
                            renderFormField("businessPotential", "Business Potential", `e.g. ${currencySymbol} 10,500`, "number", false) ||
                            renderFormField("tags", "Tags", "Enter tag and press Enter", "text", false) ||
                            renderFormField("pocUserID", "Executive", "Select Executive", "executiveSelect", false) ||
                            renderFormField("notes", "Notes", "e.g. Interested in project HP Laptop.", "textarea", false);

                        const hasVisibleExtraField = masterData.extraFields && masterData.extraFields.length > 0 &&
                            masterData.extraFields.some(fieldName => {
                                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                                return renderFormField(fieldId, fieldName, `Enter Details`, "text", false) !== null;
                            });

                        if (!hasAnyVisibleField && !hasVisibleExtraField) {
                            if (isStepTwo && (connID || uiStates.showMoreDetails)) {
                                return (
                                    <div className="w-full flex justify-start pt-2">
                                        <Button
                                            variant="link"
                                            onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                                            className="text-[--sahi-dark-green] hover:text-[--sahi-primary-color] hover:text-opacity-80 underline font-medium flex items-center gap-2 mb-2 p-0"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Select Data Field to display
                                        </Button>
                                    </div>
                                );
                            }
                            return null;
                        }

                        const visibleContent = [];

                        // Email field
                        if (!connID) {
                            const el = renderFormField("email", "Email", "e.g. john.doe@gmail.com", "email", false);
                            if (el) visibleContent.push(<div key="email" className="w-full">{el}</div>);
                        }

                        // City and State
                        const city = renderFormField("city", "City", "e.g. Mumbai", "citySelect", false);
                        const state = renderFormField("state", "State", "Select State", "stateSelect", false);
                        if (city || state) {
                            visibleContent.push(
                                <div key="city-state" className="space-y-3">
                                    {city && <div>{city}</div>}
                                    {state && <div>{state}</div>}
                                </div>
                            );
                        }

                        // Country
                        const country = renderFormField("country", "Country", "Select Country", "countrySelect", false);
                        if (country) visibleContent.push(<div key="country" className="w-full">{country}</div>);

                        // GSTIN
                        const gstin = renderFormField("gstin", taxIDLabel, "e.g. 22AAAAA0000A1Z5", "text", false);
                        if (gstin) visibleContent.push(<div key="gstin" className="w-full">{gstin}</div>);

                        // Source and Since
                        const source = renderFormField("source", "Source", "e.g. Referral", "sourceSelect", false);
                        const since = renderFormField("since", "Since", "Select Date", "date", false);
                        if (source || since) {
                            visibleContent.push(
                                <div key="source-since" className="grid grid-cols-2 gap-3">
                                    {source && <div>{source}</div>}
                                    {since && <div>{since}</div>}
                                </div>
                            );
                        }

                        // Segment and Business Potential
                        const segment = renderFormField("segment", "Category", "e.g. Enterprise", "segmentSelect", false);
                        const potential = renderFormField("businessPotential", "Business Potential", `e.g. ${currencySymbol} 10,500`, "number", false);
                        if (segment || potential) {
                            visibleContent.push(
                                <div key="segment-potential" className="space-y-3">
                                    {segment && <div className="w-full">{segment}</div>}
                                    {potential && <div className="w-3/5">{potential}</div>}
                                </div>
                            );
                        }

                        // Tags
                        const tags = renderFormField("tags", "Tags", "Enter tag and press Enter", "text", false);
                        if (tags) visibleContent.push(<div key="tags" className="w-full">{tags}</div>);

                        // Executive
                       const executive = renderFormField("pocUserID", (connID && !isStepTwo) ? "Executives" : "Executive", "Select Executive", "executiveSelect", false);                        if (executive) visibleContent.push(<div key="executive" className="w-full">{executive}</div>);

                        // Notes
                        const notes = renderFormField("notes", "Notes", "e.g. Interested in project HP Laptop.", "textarea", false);
                        if (notes) visibleContent.push(<div key="notes" className="w-full">{notes}</div>);

                        // Only render the container if there are visible fields
                        return visibleContent.length > 0 ? (
                            <div className="space-y-3">
                                {visibleContent}
                            </div>
                        ) : null;
                    })()}

                    {/* Extra Fields */}
                    {masterData.extraFields && masterData.extraFields.length > 0 && (
                        <div className="space-y-3">
                            {masterData.extraFields.map(fieldName => {
                                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                                return (
                                    <div key={fieldId} className="w-full">
                                        {renderFormField(fieldId, fieldName, `Enter Details`, "text", false)}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {editMode !== 'CONNECTION_ONLY' && connID && (
                        (() => {
                            // In CONTACTS_ONLY mode with selectedContactID, show only that contact
                            // In other modes, show all non-primary contacts or all contacts based on editMode
                            const contactsToDisplay = editMode === 'CONTACTS_ONLY' && selectedContactID 
                                ? contactsData.filter(contact => contact.CID === selectedContactID)
                                : contactsData;
                            
                            return contactsToDisplay.map((contact, filteredIndex) => {
                                // Get the actual index in the original contactsData array
                                const actualIndex = editMode === 'CONTACTS_ONLY' && selectedContactID
                                    ? contactsData.findIndex(c => c.CID === selectedContactID)
                                    : filteredIndex;
                                
                                const hasVisibleContent = true;

                                if (!hasVisibleContent) return null;

                                return (
                                    <div key={actualIndex} className={`space-y-3 ${actualIndex > 0 && !selectedContactID ? 'mt-3 pt-3' : ''}`}>
                                        <div className="space-y-3">
                                            <div className="space-y-3">
                                                {(!isStepTwo && (connID || !uiStates.showMoreDetails)) && (<div className={conFormClasses.spaceY}>
                                                    <div className="flex items-center justify-between">
                                                    <Label required={contact.isPrimary}>Contact Person</Label>
                                                    <div className="flex items-center gap-2">
                                                        {contact.isPrimary && contactsData.length > 1 && (
                                                            <span className="text-xs font-semibold text-sahi-primary-color underline">Primary</span>
                                                        )}
                                                        {!contact.isPrimary && !selectedContactID && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                                                                onClick={() => {
                                                                    setContactToDelete(actualIndex);
                                                                    setDialogStates(prev => ({ ...prev, deleteContactAlert: true }));
                                                                }}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                <Input
                                                    value={contact.name}
                                                    onChange={(e) => onContactChange(actualIndex, 'name', e.target.value)}
                                                    placeholder="e.g. John Doe"
                                                    disabled={isFieldDisabledByPermission('name')}
                                                />
                                            </div>
                                            )}

                                            {/* Mobile - ~70% Width - Hide in Step 2 */}
                                            {(!isStepTwo && (connID || !uiStates.showMoreDetails)) && (<div >
                                                <Label>Mobile</Label>
                                                <MobileInput
                                                    fieldID={`contact-mobile-${actualIndex}`}
                                                    value={contact.mobile}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (/^\d*$/.test(val)) {
                                                            onContactChange(actualIndex, 'mobile', e.target.value)
                                                        }
                                                    }}
                                                    selectedCountryCode={contact.isPrimary ? uiStates.selectedCountryCode : contact.countryCode.toString()}
                                                    onCountryCodeChange={(newCountryCode) => {
                                                        if (contact.isPrimary) {
                                                            setUIStates(prev => ({ ...prev, selectedCountryCode: newCountryCode }));
                                                            const newCountry = countries.find(c => c.CC === newCountryCode);
                                                            if (newCountry) {
                                                                onInputChange({
                                                                    target: {
                                                                        name: 'country',
                                                                        value: newCountry.Country
                                                                    }
                                                                });
                                                                if (newCountry.Country !== "India") {
                                                                    onInputChange({
                                                                        target: {
                                                                            name: 'state',
                                                                            value: ""
                                                                        }
                                                                    });
                                                                }
                                                            }
                                                        } else {
                                                            onContactChange(actualIndex, 'countryCode', newCountryCode.toString());
                                                        }
                                                    }}
                                                    placeholder="9876543210"
                                                    showLabel={false}
                                                    disabled={isFieldDisabledByPermission('mobile') || !contact.isPrimary}
                                                />
                                            </div>
                                            )}

                                            {emailPref && ((connID || uiStates.showMoreDetails) && (!isStepTwo || (isMobile && isStepTwo))) && <div className={conFormClasses.spaceY}>                                            <Label>Email</Label>
                                                <Input
                                                    value={contact.email || ""}
                                                    onChange={(e) => {
                                                        onContactChange(actualIndex, 'email', e.target.value);
                                                        if (contact.isPrimary && formData) {
                                                            onInputChange({ target: { name: 'email', value: e.target.value } });
                                                        }
                                                    }}
                                                    placeholder="john.doe@gmail.com"
                                                    type="email"
                                                    disabled={isFieldDisabledByPermission('email')}
                                                />
                                            </div>}

                                            {((contact.isPrimary && connID) || !contact.isPrimary) && !isStepTwo && (
                                                <div className={conFormClasses.spaceY}>
                                                    <Label>Designation</Label>
                                                    <Input
                                                        value={contact.designation}
                                                        onChange={(e) => onContactChange(actualIndex, 'designation', e.target.value)}
                                                        placeholder="e.g. Manager"
                                                        disabled={isFieldDisabledByPermission('designation')}

                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                            });
                        })()
                    )}



                    {/* Thread Fields (Step 2 - Only if Thread Created) - Mobile Only */}
                    {(savedThreadID && isMobile) && (() => {
                        const hasRefField = refPref;
                        const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === 1);
                        let hasCustomFields = false;
                        if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
                            try {
                                const threadCustomFields = JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD);
                                hasCustomFields = threadCustomFields.length > 0;
                            } catch (e) { }
                        }

                        if (!hasRefField && !hasCustomFields) return null;

                        return (
                            <div className="space-y-3 ">    
                                {/* Reference */}
                                {refPref && (
                                    <div className="w-full">
                                        <div className={conFormClasses.spaceY}>
                                            <Label>Reference</Label>
                                            <Input
                                                value={inquiryData?.reference || ""}
                                                onChange={(e) => onInquiryChange('reference', e.target.value)}
                                                placeholder="e.g. PO-123"
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                )}


                                {/* Thread Custom Fields */}
                                {(() => {
                                    const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === 1);
                                    let threadCustomFields = [];
                                    if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
                                        try {
                                            threadCustomFields = JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD);
                                        } catch (e) { threadCustomFields = []; }
                                    }
                                    return threadCustomFields.map(field => {
                                        const fieldId = `thr_${field.F.toLowerCase().replace(/\s+/g, '')}`;
                                        return (
                                            <div key={field.FID} className="w-full">
                                                <div className={conFormClasses.spaceY}>
                                                    <Label>{field.F}</Label>
                                                    <Input
                                                        id={fieldId}
                                                        name={fieldId}
                                                        value={formData[fieldId] || ""}
                                                        onChange={onInputChange}
                                                        placeholder={`Enter ${field.F}`}
                                                        maxLength={100}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        );
                    })()}

                    {/* Send Acknowledgement (Mobile) */}
                    {/* {editMode !== 'CONTACTS_ONLY' && !connID && !isStepTwo && isMobile && (
                        <div className="flex items-center space-x-2 my-2">
                            <input
                                type="checkbox"
                                id="sendAcknowledgementMobile"
                                name="isSendAck"
                                checked={formData.isSendAck !== undefined ? formData.isSendAck : true}
                                onChange={onInputChange}
                                className={conFormClasses.checkbox}
                            />
                            <Label htmlFor="sendAcknowledgementMobile" className="text-sm font-medium text-gray-700 cursor-pointer">
                                Send Acknowledgement
                            </Label>
                        </div>
                    )} */}

                    {/* Inquiry Section (Mobile) */}
                    {editMode !== 'CONTACTS_ONLY' && !connID && isMobile && threadTypeID !== 2 && !isBasic && (
                        <>
                            {/* More Details button removed for Step 1 */}
                            {(calledFrom === 'FORCE_INQUIRY' || calledFrom === 'THREAD_ENTRY') ? (
                                // Forced Inquiry Section (Dashboard -> Thread Entry flow)
                                renderInquirySection()
                            ) : (
                                // Optional Inquiry Section (Direct Connection Entry)
                                <div className={`${isInquiryChecked ? '!mt-[35px]' : ''}`}>
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="enterInquiryMobile"
                                                checked={isInquiryChecked}
                                                onChange={(e) => onToggleInquiry(e.target.checked)}
                                                className={conFormClasses.checkbox}
                                            />
                                            <Label htmlFor="enterInquiryMobile" className="text-sm font-medium cursor-pointer">
                                                Enter Inquiry
                                            </Label>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="enterMoreDetailsMobile"
                                                checked={isMoreDetailsChecked}
                                                onChange={(e) => onToggleMoreDetails(e.target.checked)}
                                                className={conFormClasses.checkbox}
                                            />
                                            <Label htmlFor="enterMoreDetailsMobile" className="text-sm font-medium cursor-pointer">
                                                Enter More Details
                                            </Label>
                                        </div>
                                    </div>

                                    {renderInquirySection(false, false)}
                                </div>
                            )}
                        </>
                    )}

                    {editMode !== 'CONTACTS_ONLY' && ((isBasic || threadTypeID == 2 || calledFrom === 'FORCE_INQUIRY') && !isStepTwo) && <div >
                        <div >
                            <div className="grid grid-cols-2 gap-3 mb-2">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="enterMoreDetailsMobile"
                                        checked={isMoreDetailsChecked}
                                        onChange={(e) => onToggleMoreDetails(e.target.checked)}
                                        className={conFormClasses.checkbox}
                                    />
                                    <Label htmlFor="enterMoreDetailsMobile" className="text-sm font-medium cursor-pointer">
                                        Enter More Details
                                    </Label>
                                </div>
                            </div>
                        </div>
                    </div>}

                    {/* Task and Interaction Sections - only in add mode */}
                    {editMode !== 'CONTACTS_ONLY' && !connID && (
                        <div className="space-y-4">
                            {/* Conditional Layout for Checkboxes */}
                            {hideCheckboxes ? (
                                null
                            ) : (
                                <>
                                    {/* Check if both sections are unchecked for inline display */}
                                    {!sectionStates.showLastInteraction && !sectionStates.showNextTask ? (
                                        // Inline side-by-side layout when both unchecked
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id="showLastInteraction"
                                                    checked={sectionStates.showLastInteraction}
                                                    onChange={(e) => onSectionToggle('showLastInteraction', e.target.checked)}
                                                    className={conFormClasses.checkbox}
                                                />
                                                <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showLastInteraction">
                                                    Last Interaction
                                                </Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id="showNextTask"
                                                    checked={sectionStates.showNextTask}
                                                    onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                                    className={conFormClasses.checkbox}
                                                />
                                                <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTask">
                                                    Next Task
                                                </Label>
                                            </div>
                                        </div>
                                    ) : (
                                        // Original stacked layout when at least one is checked
                                        <>
                                            {/* Last Interaction Fields - Mobile Layout */}
                                            <div className="space-y-3">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id="showLastInteraction"
                                                        checked={sectionStates.showLastInteraction}
                                                        onChange={(e) => onSectionToggle('showLastInteraction', e.target.checked)}
                                                        className={conFormClasses.checkbox}
                                                    />
                                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showLastInteraction">
                                                        Last Interaction
                                                    </Label>
                                                </div>

                                                {/* Date and Time - Half Half */}
                                                {sectionStates.showLastInteraction && (
                                                    <>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <DatePicker
                                                                fieldID="recordDate"
                                                                value={formData.recordDate ? new Date(formData.recordDate) : undefined}
                                                                onChange={(date) => onDateChange("recordDate", date)}
                                                                placeholder="Select Date"
                                                                required={isFieldRequired("recordDate")}
                                                                disableButton={!sectionStates.showLastInteraction}
                                                                minDate={null}
                                                                maxDate={new Date()}
                                                                defaultMonth={new Date()}
                                                                showLabel={false}
                                                            />
                                                            <TimeInputWithDialog
                                                                fieldID="recordTime"
                                                                value={formData.recordTime}
                                                                onChange={(timeString) => onTimeChange("recordTime", timeString)}
                                                                placeholder="Select Time"
                                                                required={isFieldRequired("recordTime")}
                                                                disabled={isFieldDisabled("recordTime")}
                                                                showLabel={false}
                                                                isMobile={true}
                                                            />
                                                        </div>
                                                        {/* Type and By - Half Half */}
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                {renderFormField("recordType", "Type", "Select Type", "recordTypeSelect", false, isFieldDisabled("recordType"))}
                                                            </div>
                                                            <div>
                                                                {renderFormField("interactionBy", "By", "Select Executive", "executiveSelect", false, isFieldDisabled("interactionBy"))}
                                                            </div>
                                                        </div>

                                                        {/* Notes - Full Width */}
                                                        <div className="w-full">
                                                            {renderFormField("recordNotes", "Notes", "e.g. Discussed contract details", "textarea", true, isFieldDisabled("recordNotes"))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Next Task Fields - Mobile Layout */}
                                            <div className="space-y-3">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id="showNextTask"
                                                        checked={sectionStates.showNextTask}
                                                        onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                                        className={conFormClasses.checkbox}
                                                    />
                                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTask">
                                                        Next Task
                                                    </Label>
                                                </div>

                                                {/* Date and Time - Half Half */}
                                                {sectionStates.showNextTask && (
                                                    <>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <DatePicker
                                                                fieldID="nextTaskDT"
                                                                value={formData.nextTaskDT ? new Date(formData.nextTaskDT) : undefined}
                                                                onChange={(date) => onDateChange("nextTaskDT", date)}
                                                                placeholder="Select Date"
                                                                required={isFieldRequired("nextTaskDT")}
                                                                disableButton={!sectionStates.showNextTask}
                                                                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                                maxDate={null}
                                                                defaultMonth={new Date()}
                                                                showLabel={false}
                                                            />
                                                            {/* <TimeInputWithDialog
                                                                fieldID="nextTaskTime"
                                                                value={formData.nextTaskTime}
                                                                onChange={(timeString) => onTimeChange("nextTaskTime", timeString)}
                                                                placeholder="Select Time"
                                                                required={isFieldRequired("nextTaskTime")}
                                                                disabled={isFieldDisabled("nextTaskTime")}
                                                                showLabel={false}
                                                                isMobile={true}
                                                            /> */}
                                                        </div>

                                                        {/* Type and Assigned to - Half Half */}
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                {renderFormField("nextTaskTyp", "Type", "Select Type", "taskTypeSelect", false, isFieldDisabled("nextTaskTyp"))}
                                                            </div>
                                                            <div>
                                                                {renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID"))}
                                                            </div>
                                                        </div>

                                                        {/* Notes - Full Width */}
                                                        <div className="w-full">
                                                            {renderFormField("nextTaskNotes", "Notes", "e.g. Arrange Meeting", "textarea", true, isFieldDisabled("nextTaskNotes"))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            {/* Share by email checkbox */}
                            {false && (
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="shareByEmail"
                                            checked={hasOwnEmail ? isEmailShared : false}
                                            disabled={!hasOwnEmail}
                                            onChange={handleCheckboxChange}
                                            className={`${conFormClasses.checkbox} ${hasOwnEmail ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                        />
                                        <label
                                            htmlFor="shareByEmail"
                                            className={`text-sm font-semibold ${hasOwnEmail ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                                            title={`${!isAdmin ? 'Ask admin to configure email' : ''}`}
                                        >
                                            Share by Email
                                        </label>
                                        {/* Settings icon - positioned right next to the label */}
                                        {!hasOwnEmail && isAdmin && (
                                            <button
                                                type="button"
                                                onClick={handleSettingsClick}
                                                className="rounded-full hover:bg-gray-100 transition-colors duration-200 p-1"
                                                title="Configure your own email"
                                            >
                                                <Settings size={16} className="text-sahi-primary-color hover:text-gray-700" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Next Task Section for Step 2 Standard Flow (Desktop/Mobile No-Inq) */}
                    {isStepTwo && ( calledFrom !== 'THREAD_ENTRY' && calledFrom !== 'FORCE_INQUIRY' && threadTypeID !== 2 && !isBasic) && (

                        <div className="!mt-6">
                            <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="showNextTaskStep2Std"
                                        checked={sectionStates.showNextTask}
                                        onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                        className={conFormClasses.checkbox}
                                    />
                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTaskStep2Std">
                                        Next Task
                                    </Label>
                                </div>
                                {sectionStates.showNextTask && (
                                    <>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                            <div className="col-span-1 sm:col-span-1">
                                                <DatePicker
                                                    fieldID="nextTaskDT"
                                                    value={formData.nextTaskDT ? new Date(formData.nextTaskDT) : undefined}
                                                    onChange={(date) => onDateChange("nextTaskDT", date)}
                                                    placeholder="Select Date"
                                                    required={isFieldRequired("nextTaskDT")}
                                                    disableButton={!sectionStates.showNextTask}
                                                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                    maxDate={null}
                                                    defaultMonth={formData.nextTaskDT ? new Date(formData.nextTaskDT) : new Date(new Date().setDate(new Date().getDate() + 1))}
                                                    showLabel={false}
                                                    label="Select Date"
                                                />
                                            </div>
                                            <div className="col-span-1 sm:col-span-1">
                                                {renderFormField("nextTaskTyp", "Type", "Select Type", "taskTypeSelect", false, isFieldDisabled("nextTaskTyp"))}
                                            </div>

                                            {/* Assigned To - Hide container if field is null */}
                                            {renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID")) && (
                                                <div className="col-span-2 sm:col-span-2">
                                                    {renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID"))}
                                                </div>
                                            )}

                                        </div>
                                        <div className="w-full">
                                            {renderFormField("nextTaskNotes", "Notes", "e.g. Arrange Meeting", "textarea", true, isFieldDisabled("nextTaskNotes"))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {editMode !== 'CONTACTS_ONLY' && (
                        <div className="w-full">
                            {renderAttachmentSection()}
                        </div>
                    )}
                </div>
            );
        }

        return (() => {
            // --- Helper: Check if Contact More Details / Extra Fields exist ---
            const hasContactMoreDetails = moreDetailsFields.some(row =>
                row.some(field => renderFormField(field.id, field.label, field.placeholder, field.type, false))
            );
            const hasContactExtraFields = masterData.extraFields && masterData.extraFields.length > 0 &&
                masterData.extraFields.some(fieldName => {
                    const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                    return renderFormField(fieldId, fieldName, `Enter Details`, "text", false) !== null;
                });
            const hasVisibleContactData = hasContactMoreDetails || hasContactExtraFields;

            // --- Helper: Check if Thread More Details / Extra Fields exist ---
            const hasThreadExtraFields = (() => {
                const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === 1);
                if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
                    try {
                        return JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD).length > 0;
                    } catch (e) { return false; }
                }
                return false;
            })();
            const hasVisibleThreadData = savedThreadID && (refPref || hasThreadExtraFields);

            // --- Component: Hyperlink to open Fields Dialog ---
            const SelectDataFieldLink = () => (
                <div className="w-full flex justify-start">
                    <Button
                        variant="link"
                        onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                        className="text-[--sahi-dark-green] hover:text-[--sahi-primary-color] hover:text-opacity-80 underline font-medium flex items-center gap-2 mb-2 p-0"
                    >
                        <Plus className="h-4 w-4" />
                        Select Data Field to display
                    </Button>
                </div>
            );

            // --- Render Logic: Step 2 in Mobile with Inquiry Checked (Split View) ---
            if (isStepTwo && isMobile && isInquiryChecked) {
                return (
                    <div className="space-y-6">
                        {/* Contact Fields Section */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-bold text-gray-900 border-b pb-1">Contact Fields</h3>
                            {hasVisibleContactData ? (
                                <div className="space-y-3">
                                    {/* Contact More Details */}
                                    {hasContactMoreDetails && (
                                        <div className="space-y-3">
                                            {moreDetailsFields.map((row, rowIndex) => {
                                                return row.map(field => {
                                                    const element = renderFormField(field.id, field.label, field.placeholder, field.type, false);
                                                    return element ? <div key={field.id} className="w-full">{element}</div> : null;
                                                });
                                            })}
                                        </div>
                                    )}
                                    {/* Contact Extra Fields */}
                                    {masterData.extraFields && masterData.extraFields.length > 0 && (
                                        <div className="space-y-3">
                                            {masterData.extraFields.map(fieldName => {
                                                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                                                return (
                                                    <div key={fieldId} className="w-full">
                                                        {renderFormField(fieldId, fieldName, `Enter Details`, "text", false)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <SelectDataFieldLink />
                            )}

                            {/* More Details Button for Contact Section */}
                            {hasVisibleContactData && (
                                <div className="mb-2">
                                    <Button
                                        variant="outline"
                                        type="button"
                                        onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                                        className={`${conFormClasses.buttonSecondary} w-full sm:w-auto`}
                                    >
                                        <Plus className="h-4 w-4" />
                                        More Details
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Inquiry Fields Section */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-bold text-gray-900 border-b pb-1">Inquiry Fields</h3>

                            {hasVisibleThreadData ? (
                                <div className="space-y-3">
                                    {/* Thread Reference */}
                                    {refPref && savedThreadID && (
                                        <div className="w-full">
                                            <div className={conFormClasses.spaceY}>
                                                <Label>Reference</Label>
                                                <Input
                                                    value={inquiryData?.reference || ""}
                                                    onChange={(e) => onInquiryChange('reference', e.target.value)}
                                                    placeholder="e.g. PO-123"
                                                    maxLength={50}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {/* Thread Custom Fields */}
                                    {savedThreadID && (() => {
                                        const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === 1);
                                        let threadCustomFields = [];
                                        if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
                                            try {
                                                threadCustomFields = JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD);
                                            } catch (e) { threadCustomFields = []; }
                                        }
                                        return threadCustomFields.map(field => {
                                            const fieldId = `thr_${field.F.toLowerCase().replace(/\s+/g, '')}`;
                                            return (
                                                <div key={field.FID} className="w-full">
                                                    <div className={conFormClasses.spaceY}>
                                                        <Label>{field.F}</Label>
                                                        <Input
                                                            id={fieldId}
                                                            name={fieldId}
                                                            value={formData[fieldId] || ""}
                                                            onChange={onInputChange}
                                                            placeholder={`Enter ${field.F}`}
                                                            maxLength={100}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            ) : (
                                <SelectDataFieldLink />
                            )}

                            {/* More Details Button for Inquiry Section */}
                            {hasVisibleThreadData && (
                                <div className="mb-2">
                                    <Button
                                        variant="outline"
                                        type="button"
                                        onClick={() => setDialogStates(prev => ({ ...prev, fields: true }))}
                                        className={`${conFormClasses.buttonSecondary} w-full sm:w-auto`}
                                    >
                                        <Plus className="h-4 w-4" />
                                        More Details
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Next Task Section (Always show in Step 2) */}
                        <div >
                            {/* Check if both sections are unchecked for inline display */}
                            <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="showNextTaskStep2"
                                        checked={sectionStates.showNextTask}
                                        onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                        className={conFormClasses.checkbox}
                                    />
                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTaskStep2">
                                        Next Task
                                    </Label>
                                </div>
                                {sectionStates.showNextTask && (
                                    <>
                                        <div className="grid grid-cols-2 gap-3">
                                            <DatePicker
                                                fieldID="nextTaskDT"
                                                value={formData.nextTaskDT ? new Date(formData.nextTaskDT) : undefined}
                                                onChange={(date) => onDateChange("nextTaskDT", date)}
                                                placeholder="Select Date"
                                                required={isFieldRequired("nextTaskDT")}
                                                disableButton={!sectionStates.showNextTask}
                                                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                maxDate={null}
                                                defaultMonth={new Date()}
                                                showLabel={false}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>{renderFormField("nextTaskTyp", "Type", "Select Type", "taskTypeSelect", false, isFieldDisabled("nextTaskTyp"))}</div>
                                            <div>{renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID"))}</div>
                                        </div>
                                        <div className="w-full">
                                            {renderFormField("nextTaskNotes", "Notes", "e.g. Arrange Meeting", "textarea", true, isFieldDisabled("nextTaskNotes"))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }

            // --- Render Logic: Standard Flow (Desktop Step 2 OR Mobile Step 2 without Inquiry) ---
            const hasBasicFields = (() => {
                const showConnType = (![2, 3].includes(connViewTypeID) && !hideFields.connType && !isStepTwo && (connID || !uiStates.showMoreDetails));
                const showBusinessName = !isStepTwo && !uiStates.showMoreDetails;
                const block1HasContent = showBusinessName || showConnType;

                const filteredBasicFields = basicFields.slice(1).filter(f => {
                    if (isStepTwo && (f.id === 'name' || f.id === 'mobile' || f.id === 'email')) return false;
                    return true;
                });
                const block2Content = !connID ? filteredBasicFields.map(field => {
                    return renderFormField(field.id, field.label, field.placeholder, field.type, true);
                }).filter(Boolean) : [];

                return block1HasContent || block2Content.length > 0;
            })();

            const hasExtraFields = masterData.extraFields && masterData.extraFields.length > 0 &&
                masterData.extraFields.some(fieldName => {
                    const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                    return renderFormField(fieldId, fieldName, `Enter Details`, "text", false) !== null;
                });

            const hasThreadFields = savedThreadID && (refPref || (() => {
                const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === 1);
                if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
                    try {
                        return JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD).length > 0;
                    } catch (e) { return false; }
                }
                return false;
            })());

            const hasMoreDetails = moreDetailsFields.some(row =>
                row.some(field => renderFormField(field.id, field.label, field.placeholder, field.type, false))
            );

            const hasContacts = connID && contactsData.some(contact => {
                return !uiStates.showMoreDetails ||
                    ((prefsEnabled.email || forceShowEmail) && !uiStates.showMoreDetails) ||
                    (masterData?.regions?.length > 0 && connViewTypeID != 5 && prefsEnabled.region && uiStates.showMoreDetails) ||
                    !contact.isPrimary;
            });

            const hasTasksOrInteractions = !connID && !hideCheckboxes;

            const hasInquiry = !connID;

            // Check if we are in Step 2 and have NO visible fields (Case 3)
            const isStepTwoEmpty = isStepTwo && !hasVisibleContactData && !hasVisibleThreadData;

            // If nothing to show, return null (unless it's Step 2 empty state)
            if (!hasBasicFields && !hasExtraFields && !hasThreadFields && !hasMoreDetails && !hasContacts && !hasTasksOrInteractions && !hasInquiry && !isStepTwoEmpty) {
                return null;
            }

            return (
                <div className="space-y-4">
                    {/* Case 3: Step 2 with no fields enabled - Show Hyperlink */}
                    {isStepTwoEmpty && (
                        <SelectDataFieldLink />
                    )}

                    {/* Desktop Layout - Keep Original */}
                    {/* Basic Fields */}
                    {hasBasicFields && editMode !== 'CONTACTS_ONLY' && (
                        <div className="space-y-3">
                            {(() => {
                                const showConnType = (![2, 3].includes(connViewTypeID) && !hideFields.connType && !isSmartSellerMode() && !isStepTwo && (connID || !uiStates.showMoreDetails) && !isFirstConnection);
                                // Calculate visible fields count for the second row to determine grid width dynamically
                                let visibleCount = 2; // Name and Mobile are typically visible by default
                                if ((prefsEnabled.email || forceShowEmail) && !hideFields.email) visibleCount++;
                                if (isDesktopInquiry) visibleCount++;
                                if (masterData?.regions?.length > 0 && connViewTypeID != 5 && prefsEnabled.region) visibleCount++;

                                // Determine required columns:
                                // Determine required columns: 
                                // 1. If ConnType is shown, we need 4 cols (2 for Name + 2 for ConnType).
                                // 2. If hidden, calculate based on visible fields (allows 3 cols for Name, Mobile, Email)
                                const isDesktopCompact = !isMobile && !connID && !isStepTwo;
                                const gridCols = isDesktopCompact ? 2 : (showConnType ? 4 : (visibleCount >= 3 ? 3 : 2));

                                // Map number to class string to ensure Tailwind compiles it
                                const gridClassMap = {
                                    2: "sm:grid-cols-2",
                                    3: "sm:grid-cols-3",
                                    4: "sm:grid-cols-4"
                                };
                                const dynamicGridClass = `grid grid-cols-1 ${gridClassMap[gridCols] || "sm:grid-cols-4"} gap-4`;
                                // Prepare Block 1 Content (Business Name & Connection Type)
                                const showBusinessName = !isStepTwo && (connID || !uiStates.showMoreDetails);
                                const block1HasContent = showBusinessName || showConnType;

                                // Prepare Block 2 Content (Other Basic Fields)
                                // Filter out fields for Step 2 (Name & Mobile & Email are hidden as they were entered in Step 1)
                                const filteredBasicFields = basicFields.slice(1).filter(f => {
                                    if (isStepTwo && (f.id === 'name' || f.id === 'mobile' || f.id === 'email')) return false;
                                    return true;
                                });

                                const block2Content = !connID ? filteredBasicFields.map(field => {
                                    const fieldElement = renderFormField(field.id, field.label, field.placeholder, field.type, true);
                                    return fieldElement ? (
                                        <div key={field.id} className={field.colSpan || conFormClasses.colSpan1}>
                                            {fieldElement}
                                        </div>
                                    ) : null;
                                }).filter(Boolean) : [];

                                return (
                                    <>
                                        {block1HasContent && (
                                            <div className={dynamicGridClass}>
                                                {isDesktopCompact ? (
                                                    <>
                                                        {showConnType &&
                                                            <div className={conFormClasses.colSpan2}>
                                                                <div className={conFormClasses.spaceY}>
                                                                    <Label className="text-sm font-medium" required={isFieldRequired('connType')}>Connection Type</Label>
                                                                    <div className={conFormClasses.flexGap}>
                                                                        {connTypes.map((option) => (
                                                                            <div key={option.value} className="flex items-center">
                                                                                <input
                                                                                    type={isInqOnlyMode ? 'radio' : 'checkbox'}
                                                                                    name={isInqOnlyMode ? 'connectionType' : option.value}
                                                                                    id={`connectionType-${option.value}`}
                                                                                    checked={isConnectionTypeSelected(option.value)}
                                                                                    onChange={(e) => onConnectionTypeChange(option.value, e.target.checked)}
                                                                                    disabled={isFieldDisabledByPermission('connType') || (isInqOnlyMode && disableConnType)}
                                                                                    className={conFormClasses.checkbox}
                                                                                />
                                                                                <Label htmlFor={`connectionType-${option.value}`} className={conFormClasses.label}>
                                                                                    {option.label}
                                                                                </Label>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                        {showBusinessName && (
                                                            <div className={conFormClasses.colSpan2}>
                                                                {renderFormField(basicFields[0].id, basicFields[0].label, basicFields[0].placeholder, basicFields[0].type, true)}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        {showBusinessName && (
                                                            <div className={conFormClasses.colSpan2}>
                                                                {renderFormField(basicFields[0].id, basicFields[0].label, basicFields[0].placeholder, basicFields[0].type, true)}
                                                            </div>
                                                        )}
                                                        {showConnType &&
                                                            <div className={conFormClasses.colSpan2}>
                                                                <div className={conFormClasses.spaceY}>
                                                                    <Label className="text-sm font-medium" required={isFieldRequired('connType')}>Connection Type</Label>
                                                                    <div className={conFormClasses.flexGap}>
                                                                        {connTypes.map((option) => (
                                                                            <div key={option.value} className="flex items-center">
                                                                                <input
                                                                                    type={isInqOnlyMode ? 'radio' : 'checkbox'}
                                                                                    name={isInqOnlyMode ? 'connectionType' : option.value}
                                                                                    id={`connectionType-${option.value}`}
                                                                                    checked={isConnectionTypeSelected(option.value)}
                                                                                    onChange={(e) => onConnectionTypeChange(option.value, e.target.checked)}
                                                                                    disabled={isFieldDisabledByPermission('connType') || (isInqOnlyMode && disableConnType)}
                                                                                    className={conFormClasses.checkbox}
                                                                                />
                                                                                <Label htmlFor={`connectionType-${option.value}`} className={conFormClasses.label}>
                                                                                    {option.label}
                                                                                </Label>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {block2Content.length > 0 && (
                                            <div className={dynamicGridClass}>
                                                {block2Content}
                                            </div>
                                        )}

                                        {/* Extra Fields - Moved to Combined Section below to allow flow with More Details */}
                                    </>
                                );
                            })()}
                            {/* Extra Fields - Merged with Basic Fields for better layout */}
                        </div>
                    )}
                    {/* More Details Toggle - Hidden as per new requirement */}

                    {/* Combined Extra Fields, More Details & Thread Fields Section */}
                    {(editMode !== "CONTACTS_ONLY") && ((hasMoreDetails || hasExtraFields) || savedThreadID) && (
                        <div className={conFormClasses.gridResponsive}>
                            {/* 1. Render More Details Fields */}
                            {(hasMoreDetails || hasExtraFields) && moreDetailsFields.flatMap((row) =>
                                row.map(field => {
                                    const element = renderFormField(field.id, field.label, field.placeholder, field.type, false);
                                    return element ? (
                                        <div key={field.id} className={field.colSpan || conFormClasses.colSpan1}>
                                            {element}
                                        </div>
                                    ) : null;
                                })
                            ).filter(Boolean)}

                            {/* 2. Render Extra Fields */}
                            {(hasMoreDetails || hasExtraFields) && (masterData.extraFields || []).map(fieldName => {
                                const fieldId = fieldName.toLowerCase().replace(/\s+/g, '');
                                const element = renderFormField(fieldId, fieldName, `Enter Details`, "text", false);
                                return element ? (
                                    <div key={fieldId} className={conFormClasses.colSpan1}>
                                        {element}
                                    </div>
                                ) : null;
                            }).filter(Boolean)}

                            {/* 3. Thread Fields (Step 2 - Only if Thread Created) */}
                            {savedThreadID && (() => {
                                const inqExtraFieldsObj = masterData.thrExtraFields && masterData.thrExtraFields.find(t => t.TID === 1);
                                let threadCustomFields = [];
                                if (inqExtraFieldsObj && inqExtraFieldsObj.Fields) {
                                    try {
                                        threadCustomFields = JSON.parse(inqExtraFieldsObj.Fields).filter(f => !f.IsD);
                                    } catch (e) { threadCustomFields = []; }
                                }

                                // Only render if Reference is enabled OR there are custom fields
                                const hasVisibleFields = refPref || threadCustomFields.length > 0;
                                if (!hasVisibleFields) return null;

                                return (
                                    <>
                                        {/* Reference */}
                                        {refPref && (
                                            <div className={isMobile ? "w-full" : conFormClasses.colSpan1}>
                                                <div className={conFormClasses.spaceY}>
                                                    <Label>Reference</Label>
                                                    <Input
                                                        value={inquiryData?.reference || ""}
                                                        onChange={(e) => onInquiryChange('reference', e.target.value)}
                                                        placeholder="e.g. PO-123"
                                                        maxLength={50}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Thread Custom Fields */}
                                        {threadCustomFields.map(field => {
                                            const fieldId = `thr_${field.F.toLowerCase().replace(/\s+/g, '')}`;
                                            return (
                                                <div key={field.FID} className={isMobile ? "w-full" : conFormClasses.colSpan1}>
                                                    <div className={conFormClasses.spaceY}>
                                                        <Label>{field.F}</Label>
                                                        <Input
                                                            id={fieldId}
                                                            name={fieldId}
                                                            value={formData[fieldId] || ""}
                                                            onChange={onInputChange}
                                                            placeholder={`Enter ${field.F}`}
                                                            maxLength={100}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            })()}
                        </div>
                    )}


                    {/* CONTACTS SECTION - Only in edit mode and when not in CONTACTS_ONLY mode (show primary contact in CONNECTION_ONLY) */}
                    {connID && !isStepTwo && (
                        contactsData.map((contact, index) => {
                            // In CONNECTION_ONLY mode, show only primary contact (index 0)
                            if (editMode === 'CONNECTION_ONLY' && contactsData.length > 1) return null;

                            const hasVisibleContent = true;

                            if (!hasVisibleContent) return null;

                            return <div className="space-y-3">
                                <div key={index} className="space-y-3">
                                    <div className={conFormClasses.gridResponsive}>
                                        {(!isStepTwo && (connID || !uiStates.showMoreDetails)) && (<div className={conFormClasses.spaceY}>
                                            <Label required={contact.isPrimary}>Contact Person</Label>
                                            <Input
                                                value={contact.name}
                                                onChange={(e) => onContactChange(index, 'name', e.target.value)}
                                                placeholder="e.g. John Doe"
                                                disabled={isFieldDisabledByPermission('name')}
                                            />
                                        </div>
                                        )}

                                        {(!isStepTwo && (connID || !uiStates.showMoreDetails)) && (<div >
                                            <Label >Mobile</Label>
                                            <MobileInput
                                                fieldID={`contact-mobile-${index}`}
                                                value={contact.mobile}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (/^\d*$/.test(val)) {
                                                        onContactChange(index, 'mobile', e.target.value)
                                                    }
                                                }}
                                                selectedCountryCode={contact.isPrimary ? uiStates.selectedCountryCode : contact.countryCode.toString()}
                                                onCountryCodeChange={(newCountryCode) => {
                                                    if (contact.isPrimary) {
                                                        setUIStates(prev => ({ ...prev, selectedCountryCode: newCountryCode }));
                                                        const newCountry = countries.find(c => c.CC === newCountryCode);
                                                        if (newCountry) {
                                                            onInputChange({
                                                                target: {
                                                                    name: 'country',
                                                                    value: newCountry.Country
                                                                }
                                                            });
                                                            if (newCountry.Country !== "India") {
                                                                onInputChange({
                                                                    target: {
                                                                        name: 'state',
                                                                        value: ""
                                                                    }
                                                                });
                                                            }
                                                        }
                                                    } else {
                                                        onContactChange(index, 'countryCode', newCountryCode.toString());
                                                    }
                                                }}
                                                placeholder="9876543210"
                                                showLabel={false}
                                                disabled={isFieldDisabledByPermission('mobile') || !contact.isPrimary}
                                            />
                                        </div>
                                        )}

                                        {((prefsEnabled.email || forceShowEmail)) && !isStepTwo && (connID || !uiStates.showMoreDetails) && (
                                            <div className={conFormClasses.spaceY}>
                                                <Label>Email</Label>
                                                <Input
                                                    value={contact.email || ""}
                                                    onChange={(e) => {
                                                        onContactChange(index, 'email', e.target.value);
                                                        // Sync primary contact email to formData
                                                        if (contact.isPrimary && formData) {
                                                            onInputChange({ target: { name: 'email', value: e.target.value } });
                                                        }
                                                    }}
                                                    placeholder="e.g. john@example.com"
                                                    type="email"
                                                    disabled={isFieldDisabledByPermission('email')}
                                                />
                                            </div>
                                        )}

                                        <div className={conFormClasses.spaceY}>
                                            <>
                                                    <Label>Designation</Label>
                                                    <div className="flex items-center gap-2">
                                                        <Input
                                                            value={contact.designation}
                                                            onChange={(e) => onContactChange(index, 'designation', e.target.value)}
                                                            placeholder="e.g. Manager"
                                                            className="flex-1"
                                                            disabled={isFieldDisabledByPermission('designation')}
                                                        />
                                                        {!contact.isPrimary && <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
                                                            onClick={() => {
                                                                setContactToDelete(index);
                                                                setDialogStates(prev => ({ ...prev, deleteContactAlert: true }));
                                                            }}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>}
                                                    </div>
                                                </>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        })
                    )}

                    {/* Task and Interaction Sections - only in add mode */}
                    {!connID && (
                        <div className="space-y-3">
                            {hideCheckboxes ? (
                                null
                            ) : (
                                <>
                                    {/* Check if both sections are unchecked for inline display */}
                                    {!sectionStates.showLastInteraction && !sectionStates.showNextTask ? (
                                        // Inline side-by-side layout when both unchecked
                                        <div className={conFormClasses.gridFullWidth}>
                                            <div className={conFormClasses.colSpan2}>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id="showLastInteraction"
                                                        checked={sectionStates.showLastInteraction}
                                                        onChange={(e) => onSectionToggle('showLastInteraction', e.target.checked)}
                                                        className={conFormClasses.checkbox}
                                                    />
                                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showLastInteraction">
                                                        Last Interaction
                                                    </Label>
                                                </div>
                                            </div>
                                            <div className={conFormClasses.colSpan2}>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id="showNextTask"
                                                        checked={sectionStates.showNextTask}
                                                        onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                                        className={conFormClasses.checkbox}
                                                    />
                                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTask">
                                                        Next Task
                                                    </Label>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Original stacked layout when at least one is checked
                                        <>
                                            {/* Last Interaction Section */}
                                            <div className={conFormClasses.gridFullWidth}>
                                                <div className={conFormClasses.colSpan2}>
                                                    <div className={conFormClasses.spaceY}>
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                id="showLastInteraction"
                                                                checked={sectionStates.showLastInteraction}
                                                                onChange={(e) => onSectionToggle('showLastInteraction', e.target.checked)}
                                                                className={conFormClasses.checkbox}
                                                            />
                                                            <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showLastInteraction">
                                                                Last Interaction
                                                            </Label>
                                                        </div>
                                                        {sectionStates.showLastInteraction && (
                                                            <DatePicker
                                                                fieldID="recordDate"
                                                                value={formData.recordDate ? new Date(formData.recordDate) : undefined}
                                                                onChange={(date) => onDateChange("recordDate", date)}
                                                                placeholder="Select Date"
                                                                required={isFieldRequired("recordDate")}
                                                                disableButton={!sectionStates.showLastInteraction}
                                                                minDate={null}
                                                                maxDate={new Date()}
                                                                defaultMonth={new Date()}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                {sectionStates.showLastInteraction && (
                                                    <>
                                                        <div className={conFormClasses.colSpan2}>
                                                            {renderFormField("recordTime", "Time", "Select Time", "time", true, isFieldDisabled("recordTime"))}
                                                        </div>
                                                        <div className={conFormClasses.colSpan2}>
                                                            {renderFormField("recordType", "Type", "Select Type", "recordTypeSelect", false, isFieldDisabled("recordType"))}
                                                        </div>
                                                        <div className={conFormClasses.colSpan2}>
                                                            {renderFormField("interactionBy", "By", "Select Executive", "executiveSelect", false, isFieldDisabled("interactionBy"))}
                                                        </div>
                                                        <div className={conFormClasses.colSpan4}>
                                                            {renderFormField("recordNotes", "Notes", "e.g. Discussed contract details", "textarea", true, isFieldDisabled("recordNotes"))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Next Task Section */}
                                            <div className={conFormClasses.gridFullWidth}>
                                                <div className={conFormClasses.colSpan2}>
                                                    <div className={conFormClasses.spaceY}>
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                id="showNextTask"
                                                                checked={sectionStates.showNextTask}
                                                                onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                                                className={conFormClasses.checkbox}
                                                            />
                                                            <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTask">
                                                                Next Task
                                                            </Label>
                                                        </div>
                                                        {sectionStates.showNextTask && (
                                                            <DatePicker
                                                                fieldID="nextTaskDT"
                                                                value={formData.nextTaskDT ? new Date(formData.nextTaskDT) : undefined}
                                                                onChange={(date) => onDateChange("nextTaskDT", date)}
                                                                placeholder="Select Date"
                                                                required={isFieldRequired("nextTaskDT")}
                                                                disableButton={!sectionStates.showNextTask}
                                                                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                                maxDate={null}
                                                                defaultMonth={new Date()}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                {sectionStates.showNextTask && (
                                                    <>
                                                        {/* <div className={conFormClasses.colSpan2}>
                                                        {renderFormField("nextTaskTime", "Time", "Select Time", "time", true, isFieldDisabled("nextTaskTime"))}
                                                    </div> */}
                                                        <div className={conFormClasses.colSpan2}>
                                                            {renderFormField("nextTaskTyp", "Type", "Select Type", "taskTypeSelect", false, isFieldDisabled("nextTaskTyp"))}
                                                        </div>
                                                        <div className={conFormClasses.colSpan2}>
                                                            {renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID"))}
                                                        </div>
                                                        <div className={conFormClasses.colSpan4}>
                                                            {renderFormField("nextTaskNotes", "Notes", "e.g. Arrange Meeting", "textarea", true, isFieldDisabled("nextTaskNotes"))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            {/* Send by email option */}
                            {false && (
                                <div className={conFormClasses.gridFullWidth}>
                                    <div className={conFormClasses.colSpan4}>
                                        <div className={conFormClasses.spaceY}>
                                            <div className="flex items-center space-x-2">
                                                {/* Always show checkbox when customer has email */}
                                                <input
                                                    type="checkbox"
                                                    id="shareByEmail"
                                                    checked={hasOwnEmail ? isEmailShared : false}
                                                    disabled={!hasOwnEmail}
                                                    onChange={handleCheckboxChange}
                                                    className={`${conFormClasses.checkbox} ${hasOwnEmail ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                                />
                                                <label
                                                    htmlFor="shareByEmail"
                                                    className={`text-sm font-semibold whitespace-nowrap ${hasOwnEmail ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                                                    title={`${!isAdmin ? 'Ask admin to configure email' : ''}`}
                                                >
                                                    Share by Email
                                                </label>
                                                {/* Settings icon - positioned right next to the label */}
                                                {!hasOwnEmail && isAdmin && (
                                                    <button
                                                        type="button"
                                                        onClick={handleSettingsClick}
                                                        className="rounded-full hover:bg-gray-100 transition-colors duration-200 p-1"
                                                        title="Configure your own email"
                                                    >
                                                        <Settings size={16} className="text-sahi-primary-color hover:text-gray-700" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    )}

                    {/* Send Acknowledgement (Desktop) */}
                    {/* {editMode !== 'CONTACTS_ONLY' && !connID && !isStepTwo && !isMobile && (
                        <div className="flex items-center space-x-2 my-2">
                            <input
                                type="checkbox"
                                id="sendAcknowledgementDesktop"
                                name="isSendAck"
                                checked={formData.isSendAck !== undefined ? formData.isSendAck : true}
                                onChange={onInputChange}
                                className={conFormClasses.checkbox}
                            />
                            <Label htmlFor="sendAcknowledgementDesktop" className="text-sm font-medium text-gray-700 cursor-pointer">
                                Send Acknowledgement
                            </Label>
                        </div>
                    )} */}

                    {/* Inquiry Section (Only for new contacts) */}
                    {!connID && calledFrom !== 'THREAD_ENTRY' && threadTypeID !== 2 && !isBasic && (
                        <>
                            {calledFrom === 'FORCE_INQUIRY' ? (
                                // Forced Inquiry Section (Dashboard -> Thread Entry flow)
                                renderInquirySection()
                            ) : (
                                // Optional Inquiry Section (Direct Connection Entry)
                                <div >
                                    <div className="flex items-center space-x-4 mb-2">
                                        { showEnterInquiry && <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="enterInquiry"
                                                checked={isInquiryChecked}
                                                onChange={(e) => onToggleInquiry(e.target.checked)}
                                                className={conFormClasses.checkbox}
                                            />
                                            <Label htmlFor="enterInquiry" className="text-sm font-medium cursor-pointer">
                                                Enter Inquiry
                                            </Label>
                                        </div>}

                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                id="enterMoreDetails"
                                                checked={isMoreDetailsChecked}
                                                onChange={(e) => onToggleMoreDetails(e.target.checked)}
                                                className={conFormClasses.checkbox}
                                            />
                                            <Label htmlFor="enterMoreDetails" className="text-sm font-medium cursor-pointer">
                                                Enter More Details
                                            </Label>
                                        </div>
                                    </div>

                                    {renderInquirySection()}
                                </div>
                            )}
                        </>
                    )}
                    {((isBasic || threadTypeID == 2 || calledFrom === 'FORCE_INQUIRY') && !isStepTwo) && <div >
                        <div className="flex items-center space-x-4 mb-2">
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="enterMoreDetails"
                                    checked={isMoreDetailsChecked}
                                    onChange={(e) => onToggleMoreDetails(e.target.checked)}
                                    className={conFormClasses.checkbox}
                                />
                                <Label htmlFor="enterMoreDetails" className="text-sm font-medium cursor-pointer">
                                    Enter More Details
                                </Label>
                            </div>
                        </div>
                    </div>}
                    {/* Inquiry Section (Mobile) */}
                    {/* Inquiry Section (Only for new contacts AND Mobile) */}
                    {!connID && isMobile && threadTypeID !== 2 && !isBasic && (
                        <>
                            {(calledFrom === 'FORCE_INQUIRY' || calledFrom === 'THREAD_ENTRY') ? (
                                // Forced Inquiry Section (Dashboard -> Thread Entry flow)
                                renderInquirySection()
                            ) : (
                                // Optional Inquiry Section (Direct Connection Entry)
                                <div >
                                    <div className="flex items-center space-x-2 mb-2">
                                        <input
                                            type="checkbox"
                                            id="enterInquiryMobile"
                                            checked={isInquiryChecked}
                                            onChange={(e) => onToggleInquiry(e.target.checked)}
                                            className={conFormClasses.checkbox}
                                        />
                                        <Label htmlFor="enterInquiryMobile" className="text-sm font-medium cursor-pointer">
                                            Enter Inquiry
                                        </Label>
                                    </div>

                                    {renderInquirySection(false, false)}
                                </div>
                            )}
                        </>
                    )}

                    {/* Next Task Section for Step 2 Standard Flow (Desktop/Mobile No-Inq) */}
                    {isStepTwo && ( calledFrom !== 'THREAD_ENTRY' && calledFrom !== 'FORCE_INQUIRY' && threadTypeID !== 2 && !isBasic) && (
                        <div className="!mt-6">
                            <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="showNextTaskStep2Std"
                                        checked={sectionStates.showNextTask}
                                        onChange={(e) => onSectionToggle('showNextTask', e.target.checked)}
                                        className={conFormClasses.checkbox}
                                    />
                                    <Label className="text-sm font-semibold text-gray-700 cursor-pointer" htmlFor="showNextTaskStep2Std">
                                        Next Task
                                    </Label>
                                </div>
                                {sectionStates.showNextTask && (
                                    <div className="grid grid-cols-1 sm:grid-cols-10 gap-4">
                                        <div className={`${conFormClasses.colSpan2}`}>
                                            <DatePicker
                                                fieldID="nextTaskDT"
                                                value={formData.nextTaskDT ? new Date(formData.nextTaskDT) : undefined}
                                                onChange={(date) => onDateChange("nextTaskDT", date)}
                                                placeholder="Select Date"
                                                required={isFieldRequired("nextTaskDT")}
                                                disableButton={!sectionStates.showNextTask}
                                                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                maxDate={null}
                                                defaultMonth={formData.nextTaskDT ? new Date(formData.nextTaskDT) : new Date(new Date().setDate(new Date().getDate() + 1))}
                                                showLabel={false}
                                                label="Select Date"
                                            />
                                        </div>
                                        {/* <div className={conFormClasses.colSpan2}>
                                            {renderFormField("nextTaskTime", "Time", "Select Time", "time", true, isFieldDisabled("nextTaskTime"))}
                                        </div> */}
                                        <div className={conFormClasses.colSpan2}>
                                            {renderFormField("nextTaskTyp", "Type", "Select Type", "taskTypeSelect", false, isFieldDisabled("nextTaskTyp"))}
                                        </div>
                                        {/* Assigned To - Hide container if field is null */}
                                        {renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID")) && (
                                            <div className={conFormClasses.colSpan2}>
                                                {renderFormField("nextTaskPocUserID", "Assigned to", "Select Executive", "executiveSelect", false, isFieldDisabled("nextTaskPocUserID"))}
                                            </div>
                                        )}
                                        <div className={conFormClasses.colSpan4}>
                                            {renderFormField("nextTaskNotes", "Notes", "e.g. Arrange Meeting", "textarea", true, isFieldDisabled("nextTaskNotes"))}
                                        </div>
                                    </div>)}
                            </div>
                        </div>
                    )}

                    {/* Email configure dialog box */}
                    <Policies open={emailConfigOpen} onOpenChange={setEmailConfigOpen} />

                    {editMode !== 'CONTACTS_ONLY' && (
                        <div className="w-full">
                            {renderAttachmentSection()}
                        </div>
                    )}
                </div>
            );
        })()
    }
    return renderDynamicFields();
};

export default ConnectionFormFields;